<template>
  <div class="screen-wrapper" ref="screenWrapper" :class="{ 'scroll-mode': isScrollMode }">
    <div class="screen-scaler" :style="screenScalerStyle">
      <div class="screen-container" :style="screenContentStyle">
        <!-- 页眉区域 -->
        <header class="screen-header">
          <div class="header-left">
            <span class="datetime">{{ currentTime }}</span>
          </div>
          <div class="header-center">
            <div class="title-glow"></div>
            <h1 class="main-title">{{ organizationName || '哲现大脑' }}</h1>
          </div>
          <div class="header-right">
          </div>
        </header>

        <!-- 主体内容区域 -->
        <main class="screen-main">
          <!-- 左侧列 -->
          <section class="screen-column column-left">
            <!-- 学校画像 - 自动切换 -->
            <div class="data-box school-profile">
              <div class="box-header-tabs">
                <h2 class="box-title">学校画像</h2>
                <div class="header-buttons">
                  <span v-for="(t, i) in visibleSchoolTabs" :key="i" class="tab-btn"
                    :class="{ active: currentSchoolTab === i }" @click="handleSchoolTabClick(i)">{{ t.label }}</span>
                </div>
              </div>
              <div class="box-content">
                <transition name="list-fade" mode="out-in">
                  <!-- 0: 组织架构 (单图) -->
                  <div
                    v-if="visibleSchoolTabs.length > 0 && visibleSchoolTabs[currentSchoolTab] && visibleSchoolTabs[currentSchoolTab].label === '组织架构'"
                    key="org" class="org-chart-container">
                    <img v-if="schoolOrgImage && !orgImageError" :src="schoolOrgImage" alt="组织架构" class="org-image"
                      @error="orgImageError = true" />
                    <div v-else class="empty-placeholder">
                      <i class="fa fa-sitemap"></i>
                      <div class="placeholder-text">暂无组织架构图</div>
                    </div>
                  </div>

                  <!-- 1: 校园概况 (左数据+右雷达) -->
                  <div
                    v-else-if="visibleSchoolTabs.length > 0 && visibleSchoolTabs[currentSchoolTab] && visibleSchoolTabs[currentSchoolTab].label === '校园概况'"
                    key="overview" class="school-overview-layout">
                    <div class="overview-left">
                      <div class="stats-card-grid">
                        <div class="stat-card">
                          <div class="sc-icon tech-icon-container">
                            <svg class="tech-svg-icon" viewBox="0 0 24 24">
                              <defs>
                                <linearGradient id="grad_teacher" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" style="stop-color:#00f2ff;stop-opacity:1" />
                                  <stop offset="100%" style="stop-color:#0055ff;stop-opacity:1" />
                                </linearGradient>
                              </defs>
                              <path fill="url(#grad_teacher)"
                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
                            </svg>
                          </div>
                          <div class="sc-info">
                            <div class="sc-val">{{ schoolOverviewStats.totalTeachers }}</div>
                            <div class="sc-lbl">本校区教师总数</div>
                          </div>
                        </div>
                        <div class="stat-card">
                          <div class="sc-icon tech-icon-container">
                            <svg class="tech-svg-icon" viewBox="0 0 24 24">
                              <defs>
                                <linearGradient id="grad_students" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" style="stop-color:#00f2ff;stop-opacity:1" />
                                  <stop offset="100%" style="stop-color:#2e00ff;stop-opacity:1" />
                                </linearGradient>
                              </defs>
                              <path fill="url(#grad_students)"
                                d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                            </svg>
                          </div>
                          <div class="sc-info">
                            <div class="sc-val">{{ schoolOverviewStats.totalStudents }}</div>
                            <div class="sc-lbl">学生总数</div>
                          </div>
                        </div>
                        <div class="stat-card">
                          <div class="sc-icon tech-icon-container">
                            <svg class="tech-svg-icon" viewBox="0 0 24 24">
                              <defs>
                                <linearGradient id="grad_grade" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" style="stop-color:#00d2ff;stop-opacity:1" />
                                  <stop offset="100%" style="stop-color:#3a7bd5;stop-opacity:1" />
                                </linearGradient>
                              </defs>
                              <path fill="url(#grad_grade)"
                                d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z" />
                            </svg>
                          </div>
                          <div class="sc-info">
                            <div class="sc-val">{{ schoolOverviewStats.totalGrades }}</div>
                            <div class="sc-lbl">年级总数</div>
                          </div>
                        </div>
                        <div class="stat-card">
                          <div class="sc-icon tech-icon-container">
                            <svg class="tech-svg-icon" viewBox="0 0 24 24">
                              <defs>
                                <linearGradient id="grad_class" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" style="stop-color:#4facfe;stop-opacity:1" />
                                  <stop offset="100%" style="stop-color:#00f2ff;stop-opacity:1" />
                                </linearGradient>
                              </defs>
                              <path fill="url(#grad_class)" d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z" />
                            </svg>
                          </div>
                          <div class="sc-info">
                            <div class="sc-val">{{ schoolOverviewStats.totalClasses }}</div>
                            <div class="sc-lbl">班级总数</div>
                          </div>
                        </div>
                      </div>
                      <div class="ratio-section">
                        <div class="ratio-bar">
                          <div class="rb-label">师生比 <span class="highlight">{{ schoolOverviewStats.teacherStudentRatio
                          }}</span> vs <span class="standard">{{ schoolOverviewStats.standardRatio }}</span> 本区标准师生比
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="overview-right">
                      <div class="chart-ref" ref="schoolRadarChart"></div>
                    </div>
                  </div>

                  <!-- 2: 校园风采 (照片墙) -->
                  <div
                    v-else-if="visibleSchoolTabs.length > 0 && visibleSchoolTabs[currentSchoolTab] && visibleSchoolTabs[currentSchoolTab].label === '校园风采'"
                    key="gallery" class="school-gallery-grid" ref="schoolGallery">
                    <div class="gallery-item" v-for="(item, index) in schoolPhotoGallery" :key="index">
                      <div class="g-img" :style="{ backgroundImage: item.image ? `url(${item.image})` : '' }">
                        <span v-if="!item.image" class="placeholder-icon"><i class="fa fa-image"></i></span>
                      </div>
                      <div class="g-title">{{ item.title }}</div>
                    </div>
                  </div>

                  <!-- 暂无数据 -->
                  <div v-else key="empty" class="empty-placeholder">
                    <i class="el-icon-document-delete"></i>
                    <span>暂无数据</span>
                  </div>
                </transition>
              </div>
            </div>

            <!-- 教师画像 - 自动切换 (6个模块) -->
            <div class="data-box teacher-profile">
              <div class="box-header-tabs">
                <h2 class="box-title">教师画像</h2>
                <div class="header-buttons">
                  <span v-for="(t, i) in visibleTeacherTabs" :key="i" class="tab-btn"
                    :class="{ active: currentTeacherTab === i }" @click="handleTeacherTabClick(i)">{{ t.label }}</span>
                </div>
              </div>

              <div class="box-content chart-box">
                <transition name="list-fade" mode="out-in">
                  <!-- 0: 教师概况 (左侧统计 + 中间柱状图 + 右侧环形图) -->
                  <div
                    v-if="visibleTeacherTabs.length > 0 && visibleTeacherTabs[currentTeacherTab] && visibleTeacherTabs[currentTeacherTab].label === '教师概况'"
                    key="overview" class="teacher-overview-layout">
                    <!-- 左侧:教师数量统计 -->
                    <div class="stats-panel">
                      <div class="stat-item">
                        <div class="stat-icon">
                          <svg class="tech-svg-icon" viewBox="0 0 24 24">
                            <path fill="currentColor"
                              d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                          </svg>
                        </div>
                        <div class="stat-info">
                          <div class="stat-label">全校教师工总数</div>
                          <div class="stat-value">{{ teacherOverviewData.total }}</div>
                        </div>
                      </div>
                      <div class="stat-item">
                        <div class="stat-icon male">
                          <svg class="tech-svg-icon" viewBox="0 0 24 24">
                            <path fill="currentColor"
                              d="M20 2v6h-2V5.41l-4.29 4.29c.67 1.17 1 2.5 1 3.93 0 4.41-3.59 8-8 8s-8-3.59-8-8 3.59-8 8-8c1.43 0 2.76.33 3.93 1L18.59 2H20zM6.67 11.67C5.04 13.3 5.04 15.96 6.67 17.59s4.29 1.63 5.92 0 1.63-4.29 0-5.92-4.29-1.63-5.92 0z" />
                          </svg>
                        </div>
                        <div class="stat-info">
                          <div class="stat-label">男教师</div>
                          <div class="stat-value">{{ teacherOverviewData.male }}</div>
                        </div>
                      </div>
                      <div class="stat-item">
                        <div class="stat-icon female">
                          <svg class="tech-svg-icon" viewBox="0 0 24 24">
                            <path fill="currentColor"
                              d="M12 4c-3.31 0-6 2.69-6 6 0 2.97 2.16 5.44 5 5.92V18H8v2h3v2h2v-2h3v-2h-3v-2.08c2.84-.48 5-2.95 5-5.92 0-3.31-2.69-6-6-6zm0 2c2.21 0 4 1.79 4 4s-1.79 4-4 4-4-1.79-4-4 1.79-4 4-4z" />
                          </svg>
                        </div>
                        <div class="stat-info">
                          <div class="stat-label">女教师</div>
                          <div class="stat-value">{{ teacherOverviewData.female }}</div>
                        </div>
                      </div>
                    </div>
                    <!-- 中间:教师任教年级分布(横向柱状图) -->
                    <div class="chart-middle">
                      <div class="chart-ref" ref="teacherChart1"></div>
                    </div>
                    <!-- 右侧:教师学科分布(环形图) -->
                    <div class="chart-right">
                      <div class="chart-ref" ref="teacherChart2"></div>
                    </div>
                  </div>

                  <!-- 1: 分级对比 (3个环形图) -->
                  <div
                    v-else-if="visibleTeacherTabs.length > 0 && visibleTeacherTabs[currentTeacherTab] && visibleTeacherTabs[currentTeacherTab].label === '分级对比'"
                    key="classification" class="chart-triple-container">
                    <div class="chart-third">
                      <div class="chart-ref" ref="teacherClassChart1"></div>
                    </div>
                    <div class="chart-third">
                      <div class="chart-ref" ref="teacherClassChart2"></div>
                    </div>
                    <div class="chart-third">
                      <div class="chart-ref" ref="teacherClassChart3"></div>
                    </div>
                  </div>

                  <!-- 3: 名特优 (照片墙) -->
                  <div
                    v-else-if="visibleTeacherTabs.length > 0 && visibleTeacherTabs[currentTeacherTab] && visibleTeacherTabs[currentTeacherTab].label === '名特优'"
                    key="famous" :class="famousTeachers.length > 0 ? 'photo-grid' : 'empty-placeholder'"
                    ref="famousTeacherList" @mouseenter="isScrolling = false" @mouseleave="isScrolling = true">
                    <template v-if="famousTeachers.length > 0">
                      <div class="photo-item" v-for="(teacher, i) in famousTeachers" :key="i">
                        <div class="photo-img" :style="{ backgroundImage: `url(${teacher.photo})` }">
                          <span v-if="!teacher.photo" class="placeholder-svg">
                            <div class="placeholder-svg-container" role="img" aria-label="无照片">
                              <svg viewBox="0 0 64 64" class="placeholder-svg-icon" xmlns="http://www.w3.org/2000/svg"
                                focusable="false" aria-hidden="true">
                                <circle cx="32" cy="20" r="12" fill="none" stroke="#0a113a" stroke-width="3" />
                                <path d="M8 56c4-12 20-16 24-16s20 4 24 16" fill="none" stroke="#0a113a"
                                  stroke-width="3" stroke-linecap="round" />
                              </svg>
                            </div>
                          </span>
                        </div>
                        <div class="photo-info">
                          <div class="p-name nav-label">
                            <span class="p-name-text">{{ teacher.name }}</span>
                            <span v-if="(teacher.departmentName || teacher.DepartmentName)" class="p-dept-inline">{{
                              teacher.departmentName || teacher.DepartmentName }}</span>
                          </div>
                          <div class="p-title">{{ teacher.title }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="empty-placeholder"
                        style="height: 100%; flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                        <div class="placeholder-text">暂无数据</div>
                      </div>
                    </template>
                  </div>

                  <!-- 4: 教师光荣榜 (照片墙) -->
                  <div
                    v-else-if="visibleTeacherTabs.length > 0 && visibleTeacherTabs[currentTeacherTab] && visibleTeacherTabs[currentTeacherTab].label === '教师光荣榜'"
                    key="honor" :class="honorTeachers.length > 0 ? 'photo-grid' : 'empty-placeholder'"
                    ref="honorTeacherList" @mouseenter="isScrolling = false" @mouseleave="isScrolling = true">
                    <template v-if="honorTeachers.length > 0">
                      <div class="photo-item" v-for="(teacher, i) in honorTeachers" :key="i">
                        <div class="photo-img" :style="{ backgroundImage: `url(${teacher.photo})` }">
                          <span v-if="!teacher.photo" class="placeholder-svg">
                            <div class="placeholder-svg-container" role="img" aria-label="无照片">
                              <svg viewBox="0 0 64 64" class="placeholder-svg-icon" xmlns="http://www.w3.org/2000/svg"
                                focusable="false" aria-hidden="true">
                                <circle cx="32" cy="20" r="12" fill="none" stroke="#0a113a" stroke-width="3" />
                                <path d="M8 56c4-12 20-16 24-16s20 4 24 16" fill="none" stroke="#0a113a"
                                  stroke-width="3" stroke-linecap="round" />
                              </svg>
                            </div>
                          </span>
                        </div>
                        <div class="photo-info">
                          <div class="p-name nav-label">
                            <span class="p-name-text">{{ teacher.name }}</span>
                            <span v-if="(teacher.departmentName || teacher.DepartmentName)" class="p-dept-inline">{{
                              teacher.departmentName || teacher.DepartmentName }}</span>
                          </div>
                          <div class="p-title">{{ teacher.title }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="empty-placeholder"
                        style="height: 100%; flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                        <div class="placeholder-text">暂无数据</div>
                      </div>
                    </template>
                  </div>

                  <!-- 5: 教师形象墙 (照片墙) -->
                  <div
                    v-else-if="visibleTeacherTabs.length > 0 && visibleTeacherTabs[currentTeacherTab] && visibleTeacherTabs[currentTeacherTab].label === '教师形象墙'"
                    key="image-wall" :class="wallTeachers.length > 0 ? 'photo-grid' : 'empty-placeholder'"
                    ref="wallTeacherList" @mouseenter="isScrolling = false" @mouseleave="isScrolling = true">
                    <template v-if="wallTeachers.length > 0">
                      <div class="photo-item" v-for="(teacher, i) in wallTeachers" :key="i">
                        <div class="photo-img" :style="{ backgroundImage: `url(${teacher.photo})` }">
                          <span v-if="!teacher.photo" class="placeholder-svg">
                            <div class="placeholder-svg-container" role="img" aria-label="无照片">
                              <svg viewBox="0 0 64 64" class="placeholder-svg-icon" xmlns="http://www.w3.org/2000/svg"
                                focusable="false" aria-hidden="true">
                                <circle cx="32" cy="20" r="12" fill="none" stroke="#0a113a" stroke-width="3" />
                                <path d="M8 56c4-12 20-16 24-16s20 4 24 16" fill="none" stroke="#0a113a"
                                  stroke-width="3" stroke-linecap="round" />
                              </svg>
                            </div>
                          </span>
                        </div>
                        <div class="photo-info">
                          <div class="p-name nav-label">
                            <span class="p-name-text">{{ teacher.name }}</span>
                            <span v-if="(teacher.departmentName || teacher.DepartmentName)" class="p-dept-inline">{{
                              teacher.departmentName || teacher.DepartmentName }}</span>
                          </div>
                          <div class="p-title">{{ teacher.title }}</div>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="empty-placeholder"
                        style="height: 100%; flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                        <div class="placeholder-text">暂无数据</div>
                      </div>
                    </template>
                  </div>

                  <!-- 全局暂无数据 -->
                  <div v-else key="empty" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center;">
                    <i class="el-icon-document-delete"></i>
                    <div class="placeholder-text">暂无数据</div>
                  </div>
                </transition>
              </div>
            </div>
          </section>

          <!-- 中间列 -->
          <section class="screen-column column-center">

            <div class="data-box center-data-box" style="display: flex; flex-direction: column; padding: 24px;">
              <div class="box-header-tabs" style="justify-content: center; margin-bottom: 20px; padding-top: 8px;">
                <div class="header-buttons">
                  <span v-for="(t, i) in visibleCenterTabs" :key="i" class="tab-btn"
                    :class="{ active: currentCenterTab === i }" @click="handleCenterTabClick(i)">{{ t.label }}</span>
                </div>
              </div>

              <div class="center-content"
                style="flex: 1; display: flex; flex-direction: column; overflow: hidden; min-height: 0;">
                <transition name="list-fade" mode="out-in">
                  <!-- 0: 校园新风 -->
                  <div
                    v-if="visibleCenterTabs.length > 0 && visibleCenterTabs[currentCenterTab] && visibleCenterTabs[currentCenterTab].label === '校园新风'"
                    key="newStyle" class="center-module campus-fresh-wind-wrapper">
                    <!-- 核心图文区（拥有唯一边框） -->
                    <div class="campus-top-section">
                      <div class="campus-top-imgs">
                        <div class="carousel-container">
                          <el-carousel height="100%" direction="horizontal" indicator-position="none"
                            style="height: 100%; width: 100%;">
                            <el-carousel-item v-for="(item, index) in campusFreshWindData.carouselImages" :key="index">
                              <div class="carousel-img-placeholder"
                                :style="{ backgroundImage: item ? 'url(' + item + ')' : '' }">
                                <span v-if="!item" class="placeholder-text">校园实景图展示</span>
                              </div>
                            </el-carousel-item>
                          </el-carousel>
                        </div>
                      </div>

                      <div class="school-intro-box">
                        <p>{{ campusFreshWindData.schoolIntro || '暂无学校简介...' }}</p>
                      </div>
                    </div>

                    <!-- 下方：六边形统计网格 -->
                    <div class="fresh-wind-hex-grid">
                      <div class="hex-wrap" v-for="(val, index) in [
                        { label: '教学楼', val: campusFreshWindData.teachingBuildingCount },
                        { label: '人工智能教室', val: campusFreshWindData.aiChemistryLabCount },
                        { label: '科学实验室', val: campusFreshWindData.scienceLabCount },
                        { label: '艺术空间', val: campusFreshWindData.artSpaceCount },
                        { label: '约劳耕园', val: campusFreshWindData.modelFarmCount },
                        { label: '运动场/馆', val: campusFreshWindData.stadiumCount },
                        { label: '报告厅', val: campusFreshWindData.lectureHallCount },
                        { label: '校园食堂', val: campusFreshWindData.cafeteriaCount }
                      ]" :key="index">
                        <div class="hex-item">
                          <div class="hex-inner">
                            <div class="hex-circle"></div>
                            <span class="stat-num">{{ val.val }}</span>
                          </div>
                        </div>
                        <div class="hex-label">{{ val.label }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- 1: 校园动态 -->
                  <div
                    v-else-if="visibleCenterTabs.length > 0 && visibleCenterTabs[currentCenterTab] && visibleCenterTabs[currentCenterTab].label === '校园动态'"
                    key="dynamic" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-data-line"></i>
                    <div class="placeholder-text">校园动态模块开发中</div>
                  </div>

                  <!-- 2: 教师成长 -->
                  <div
                    v-else-if="visibleCenterTabs.length > 0 && visibleCenterTabs[currentCenterTab] && visibleCenterTabs[currentCenterTab].label === '教师成长'"
                    key="teacher-growth" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-s-custom"></i>
                    <div class="placeholder-text">教师成长模块开发中</div>
                  </div>

                  <!-- 3: 学生成长 -->
                  <div
                    v-else-if="visibleCenterTabs.length > 0 && visibleCenterTabs[currentCenterTab] && visibleCenterTabs[currentCenterTab].label === '学生成长'"
                    key="student-growth" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-medal-1"></i>
                    <div class="placeholder-text">学生成长模块开发中</div>
                  </div>

                  <!-- 其他动态开发中模块提示 -->
                  <div v-else-if="visibleCenterTabs.length > 0 && visibleCenterTabs[currentCenterTab]"
                    :key="'center-dev-' + visibleCenterTabs[currentCenterTab].label" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-loading"></i>
                    <div class="placeholder-text">{{ visibleCenterTabs[currentCenterTab].label }} 正在开发中</div>
                  </div>

                  <!-- 暂无数据 -->
                  <div v-else key="empty" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-document-delete"></i>
                    <div class="placeholder-text">暂无数据</div>
                  </div>
                </transition>
              </div>
            </div>
          </section>

          <!-- 右侧列 -->
          <section class="screen-column column-right">
            <div class="data-box class-profile">
              <div class="box-header-tabs">
                <div class="header-left">
                  <h2 class="box-title">班级画像</h2>
                  <!-- 级联选择器 -->
                  <div class="class-selector">
                    <el-cascader v-model="selectedClass" :options="classSelectOptions"
                      :props="{ expandTrigger: 'hover' }" @change="handleClassChange"
                      popper-class="tech-cascader-popper" size="small"></el-cascader>
                  </div>
                </div>
                <div class="header-buttons">
                  <span v-for="(t, i) in visibleClassTabs" :key="i" class="tab-btn"
                    :class="{ active: currentClassTab === i }" @click="handleClassTabClick(i)">{{ t.label }}</span>
                </div>
              </div>
              <div class="box-content class-profile-content">
                <transition name="list-fade" mode="out-in">
                  <!-- 0: 班级风采 -->
                  <div
                    v-if="visibleClassTabs.length > 0 && visibleClassTabs[currentClassTab] && visibleClassTabs[currentClassTab].label === '班级风采'"
                    key="style" style="height: 100%; display: flex; width: 100%; gap: 15px; padding: 0 10px;">
                    <!-- 左侧: 班主任信息 -->
                    <div class="cp-left">
                      <div class="teacher-card">
                        <div class="tc-header">
                          <div class="tc-info">
                            <div class="tc-name">{{ classProfileData.teacherName }}</div>
                            <div class="tc-role">{{ classProfileData.teacherRole }}</div>
                          </div>
                          <div class="tc-photo-wrapper">
                            <img v-if="classProfileData.teacherPhoto" :src="classProfileData.teacherPhoto"
                              class="tc-photo-img" />
                            <div v-else class="tc-photo-placeholder"><i class="fa fa-user"></i></div>
                          </div>
                        </div>
                        <div class="tc-message">
                          <div class="tm-title">班主任寄语</div>
                          <div class="tm-content">{{ classProfileData.teacherMessage }}</div>
                        </div>
                      </div>
                    </div>

                    <!-- 右侧: 轮播展示 -->
                    <div class="cp-right">
                      <div class="cp-carousel-container">
                        <div class="cp-carousel-content">
                          <el-carousel ref="classCarousel" height="100%" indicator-position="none"
                            :interval="classCarouselSwitchTime * 1000" @change="handleClassCarouselChange">
                            <el-carousel-item key="badge">
                              <div class="cp-slide-item">
                                <div class="cp-img-wrapper">
                                  <img v-if="classProfileData.classBadge" :src="classProfileData.classBadge" />
                                  <div v-else class="empty-placeholder">
                                    <i class="fa fa-shield"></i>
                                    <div class="placeholder-text">暂无图片</div>
                                  </div>
                                </div>
                              </div>
                            </el-carousel-item>
                            <el-carousel-item key="photo">
                              <div class="cp-slide-item">
                                <div class="cp-img-wrapper">
                                  <img v-if="classProfileData.classGroupPhoto"
                                    :src="classProfileData.classGroupPhoto" />
                                  <div v-else class="empty-placeholder">
                                    <i class="fa fa-users"></i>
                                    <div class="placeholder-text">暂无图片</div>
                                  </div>
                                </div>
                              </div>
                            </el-carousel-item>
                          </el-carousel>
                        </div>
                        <!-- 自定义文字指示器 -->
                        <div class="cp-carousel-indicators">
                          <div class="cp-indicator-item" :class="{ active: currentClassCarouselIndex === 0 }"
                            @click="switchClassCarousel(0)">
                            班徽
                          </div>
                          <div class="cp-indicator-item" :class="{ active: currentClassCarouselIndex === 1 }"
                            @click="switchClassCarousel(1)">
                            班级合照
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 1: 班级荣誉 -->
                  <div
                    v-else-if="visibleClassTabs.length > 0 && visibleClassTabs[currentClassTab] && visibleClassTabs[currentClassTab].label === '班级荣誉'"
                    key="honor" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-medal-1"></i>
                    <div class="placeholder-text">班级荣誉模块开发中</div>
                  </div>

                  <!-- 2: 值周评分 -->
                  <div
                    v-else-if="visibleClassTabs.length > 0 && visibleClassTabs[currentClassTab] && visibleClassTabs[currentClassTab].label === '值周评分'"
                    key="score" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-set-up"></i>
                    <div class="placeholder-text">值周评分模块开发中</div>
                  </div>

                  <div v-else-if="visibleClassTabs.length > 0 && visibleClassTabs[currentClassTab]"
                    :key="visibleClassTabs[currentClassTab].label" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-loading"></i>
                    <div class="placeholder-text">{{ visibleClassTabs[currentClassTab].label }} 正在开发中</div>
                  </div>

                  <!-- 暂无数据 -->
                  <div v-else key="empty" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column;">
                    <i class="el-icon-document-delete"></i>
                    <div class="placeholder-text">暂无数据</div>
                  </div>
                </transition>
              </div>
            </div>
            <!-- 学生画像 - 自动切换 -->
            <div class="data-box student-profile">
              <div class="box-header-tabs">
                <div class="header-left" style="display: flex; align-items: center; gap: 15px;">
                  <h2 class="box-title">学生画像</h2>
                  <!-- 班级选择器 -->
                  <div class="student-class-selector class-selector">
                    <el-cascader v-model="selectedStudentClass" :options="studentSelectOptions"
                      :props="{ expandTrigger: 'hover' }" @change="handleStudentClassChange"
                      popper-class="tech-cascader-popper" size="small" placeholder="选择班级">
                      <template slot-scope="{ node, data }">
                        <span>{{ data.label }}</span>
                      </template>
                    </el-cascader>
                  </div>
                </div>
                <div class="header-buttons">
                  <span v-for="(t, i) in visibleStudentTabs" :key="i" class="tab-btn"
                    :class="{ active: currentStudentTab === i }" @click="handleStudentTabClick(i)">{{ t.label }}</span>
                </div>
              </div>

              <div class="box-content">
                <transition name="list-fade" mode="out-in">
                  <!-- 点评榜单 -->
                  <div
                    v-if="visibleStudentTabs.length > 0 && visibleStudentTabs[currentStudentTab] && visibleStudentTabs[currentStudentTab].label === '点评榜单'"
                    key="leaderboard" class="leaderboard-container">
                    <div class="leader-columns">
                      <!-- 左侧: 得分榜 -->
                      <div class="leader-col score-col">
                        <div class="col-title">得分榜</div>
                        <div class="col-header">
                          <span class="h-rank">排名</span>
                          <span class="h-name">姓名</span>
                          <span class="h-score">分数</span>
                        </div>
                        <div class="col-list-scroll" ref="scoreList" @mouseenter="isScrolling = false"
                          @mouseleave="isScrolling = true">
                          <div class="list-wrapper">
                            <div class="list-item" v-for="(student, index) in studentProfileData.evaluation.scoreList"
                              :key="'s-' + index">
                              <span class="rank">
                                <i v-if="student.rank <= 3" class="fa fa-trophy"
                                  :style="{ color: student.rank === 1 ? '#ffd700' : (student.rank === 2 ? '#c0c0c0' : '#cd7f32') }"></i>
                                <span v-else>{{ student.rank }}</span>
                              </span>
                              <span class="name">{{ student.name }}</span>
                              <span class="score">{{ student.score }}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- 右侧: 单项榜 -->
                      <div class="leader-col single-col">
                        <div class="col-title">单项榜</div>
                        <div class="col-list-scroll" ref="singleList" @mouseenter="isScrolling = false"
                          @mouseleave="isScrolling = true">
                          <div class="list-wrapper">
                            <div class="single-item-group"
                              v-for="(item, idx) in studentProfileData.evaluation.singleItemList" :key="'g-' + idx">
                              <div class="group-header">
                                <span class="g-name">{{ item.itemName }}</span>
                                <span class="g-score">{{ item.score }}</span>
                              </div>
                              <div class="group-children">
                                <div class="child-item" v-for="(child, cIdx) in item.children" :key="'c-' + cIdx">
                                  <span class="c-rank">{{ child.rank }}</span>
                                  <span class="c-name">{{ child.name }}</span>
                                  <span class="c-score">{{ child.score }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 协约之星 -->
                  <div
                    v-else-if="visibleStudentTabs.length > 0 && visibleStudentTabs[currentStudentTab] && visibleStudentTabs[currentStudentTab].label === '协约之星'"
                    key="star" class="star-student-list" @mouseenter="isScrolling = false"
                    @mouseleave="isScrolling = true">
                    <div class="star-list-header">
                      <span class="h-rank">排名</span>
                      <span class="h-name">姓名</span>
                      <span class="h-score">分数</span>
                    </div>
                    <div class="star-list-content" ref="starList">
                      <div class="list-wrapper">
                        <div class="star-row" v-for="(student, index) in studentProfileData.stars" :key="'st-' + index">
                          <span class="rank">{{ student.rank }}</span>
                          <span class="name">{{ student.name }}</span>
                          <span class="score">{{ student.score }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 综合素质 -->
                  <div
                    v-else-if="visibleStudentTabs.length > 0 && visibleStudentTabs[currentStudentTab] && visibleStudentTabs[currentStudentTab].label === '综合素质'"
                    key="quality" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center;">
                    <i class="el-icon-medal-1"></i>
                    <div class="placeholder-text">综合素质模块开发中</div>
                  </div>

                  <!-- 学生笑脸墙 -->
                  <div
                    v-else-if="visibleStudentTabs.length > 0 && visibleStudentTabs[currentStudentTab] && visibleStudentTabs[currentStudentTab].label === '学生笑脸墙'"
                    key="smile-wall" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center;">
                    <i class="el-icon-picture"></i>
                    <div class="placeholder-text">学生笑脸墙模块开发中</div>
                  </div>

                  <!-- 暂无数据 -->
                  <div v-else key="empty" class="empty-placeholder"
                    style="flex: 1; height: 100%; display: flex; align-items: center; justify-content: center;">
                    <i class="el-icon-document-delete"></i>
                    <div class="placeholder-text">暂无数据</div>
                  </div>
                </transition>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts';
import { getBigScreenMenuConfig, getSchoolProfileData, getTeacherProfileData, getCampusFreshWindData, getBigScreenClasses, getBigScreenClassProfile } from '../api/modules/big-screen';
import { BaseApiUrl } from '../api/base';

export default {
  components: {
  },
  name: 'SchoolInfoScreen',
  data() {
    return {
      width: 3600,
      height: 1080,
      scale: 1,
      isScrollMode: false,
      currentTime: '',
      classTabNames: [],
      classCarouselSwitchtime: 15,
      BaseApiUrl: BaseApiUrl,
      // 学校画像
      currentSchoolTab: 0,
      hoverSchoolTab: -1,
      schoolTabNames: [],
      organizationName: '',
      schoolOrgImage: '',
      orgImageError: false,
      schoolOverviewStats: {},
      schoolRadarData: [],
      schoolPhotoGallery: [],

      // 教师画像
      currentTeacherTab: 0,
      hoverTeacherTab: -1,
      isHoveringTeacher: false,
      teacherTabNames: [],
      teacherOverviewData: {},
      famousTeachers: [],
      honorTeachers: [],
      wallTeachers: [],
      teacherClassificationData: {},
      teacherAwardsData: {},

      // 综合展示(中间列)
      centerTabNames: [],
      currentCenterTab: 0,
      campusFreshWindData: {
        carouselImages: ['', '', ''],
        schoolIntro: '',
        teachingBuildingCount: 0,
        aiChemistryLabCount: 0,
        scienceLabCount: 0,
        artSpaceCount: 0,
        modelFarmCount: 0,
        stadiumCount: 0,
        lectureHallCount: 0,
        cafeteriaCount: 0
      },

      // 班级画像
      classSelectOptions: [],
      selectedClass: [],
      classProfileData: {},
      currentClassTab: 0,
      currentClassCarouselIndex: 0,
      carouselImages: [],

      // 学生画像
      studentTabNames: [],
      currentStudentTab: 0,
      studentSelectOptions: [],
      selectedStudentClass: [],
      studentProfileData: {
        evaluation: {
          scoreList: [],
          singleItemList: []
        },
        stars: []
      },
      studentDataMap: {},
      studentDataSets: [],

      // 滚动相关
      scrollTimer: null,
      isScrolling: true,
      scrollPositions: {
        score: 0,
        single: 0,
        stars: 0,
        school: 0,
        famous: 0,
        honor: 0,
        wall: 0
      },

      // 其他
      schoolIntro: '',
      navButtons: [],
      charts: { main: null, t1: null, t2: null, c3: null, c1: null, c2: null, a1: null, a2: null, s_radar: null },

      // 统一时间与事件系统
      eventFunctions: new Map(),
      systemTime: '',
      timer: null
    }
  },
  computed: {
    screenScalerStyle() {
      if (this.isScrollMode) {
        return {
          width: `${3600 * this.scale}px`,
          height: `${1080 * this.scale}px`,
          position: 'relative'
        }
      }
      return {
        width: '100%',
        height: '100%',
        position: 'relative'
      }
    },
    screenContentStyle() {
      if (this.isScrollMode) {
        return {
          width: `${this.width}px`,
          height: `${this.height}px`,
          transform: `scale(${this.scale})`,
          transformOrigin: 'left top',
          position: 'absolute',
          top: 0,
          left: 0
        }
      }
      return {
        width: `${this.width}px`,
        height: `${this.height}px`,
        transform: `translate(-50%, -50%) scale(${this.scale})`,
        transformOrigin: 'center center',
        top: '50%',
        left: '50%',
        position: 'absolute'
      }
    },
    visibleSchoolTabs() {
      return this.schoolTabNames ? this.schoolTabNames.filter(t => t.visible) : [];
    },
    visibleTeacherTabs() {
      return this.teacherTabNames ? this.teacherTabNames.filter(t => t.visible) : [];
    },
    visibleStudentTabs() {
      return this.studentTabNames ? this.studentTabNames.filter(t => t.visible) : [];
    },
    visibleClassTabs() {
      return this.classTabNames ? this.classTabNames.filter(t => t.visible) : [];
    },
    visibleCenterTabs() {
      return this.centerTabNames ? this.centerTabNames.filter(t => t.visible) : [];
    },

    currentStudentData() {
      if (!this.visibleStudentTabs || this.visibleStudentTabs.length === 0) return [];
      const currentTab = this.visibleStudentTabs[this.currentStudentTab];
      if (!currentTab) return [];
      const originalIndex = this.studentTabNames.indexOf(currentTab);
      return this.studentDataSets[originalIndex] || [];
    }
  },
  async mounted() {
    await this.initData();
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    // Save original data for resetting lists
    this.originalSchoolPhotoGallery = [...this.schoolPhotoGallery];
    this.originalFamousTeachers = [...this.famousTeachers];
    this.originalHonorTeachers = [...this.honorTeachers];
    this.originalWallTeachers = [...this.wallTeachers];

    // 初始化时间同步和事件系统
    this.startTimeSync();

    // 启动各模块独立轮播
    this.scheduleNextRotation('School');
    this.scheduleNextRotation('Teacher');
    this.scheduleNextRotation('Student');
    this.scheduleNextRotation('Center');
    this.scheduleNextRotation('Class');

    // 启动班级轮播
    this.addEvent('rotateClass', this.classSwitchTime || 10);

    // 修复: 确保DOM渲染完成后再初始化图表
    // 增加延时以等待页面初始动画完成
    setTimeout(() => {
      this.initTeacherChart();
    }, 500);

    // 初始化学生画像的班级选择项 (复制一份)
    this.studentSelectOptions = JSON.parse(JSON.stringify(this.classSelectOptions));

    // 初始化学生数据
    this.handleStudentClassChange(this.selectedStudentClass);
    this.reloadStudentTabData();

    // 启动自动滚动
    this.startAutoScroll();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);

    if (this.timer) clearInterval(this.timer);
    if (this.scrollTimer) clearInterval(this.scrollTimer);

    this.disposeCharts();
  },
  watch: {
    currentSchoolTab() {
      this.reloadSchoolTabData();
      if (this.currentSchoolTab === 1) {
        setTimeout(() => this.initSchoolRadarChart(), 50);
      }
    },
    currentTeacherTab() {
      this.reloadTeacherTabData();
      // 当标签切换时,延迟初始化图表以等待 transition 完成
      if (this.currentTeacherTab === 0) {
        setTimeout(() => this.initTeacherChart(), 50);
      } else if (this.currentTeacherTab === 1) {
        setTimeout(() => this.initClassificationCharts(), 50);
      }
    },
    currentStudentTab() {
      this.reloadStudentTabData();
    },

  },
  methods: {
    async initData() {
      // 从 URL 查询参数获取 departmentId，例如 ?departmentId=1
      const urlParams = new URLSearchParams(window.location.search);
      const deptId = urlParams.get('departmentId') || 0;

      // --- 从后端获取菜单显隐配置 ---
      let apiConfig = null;
      try {
        const res = await getBigScreenMenuConfig({ departmentId: deptId });
        if (res && res.data && res.data.success && res.data.response && res.data.response.MenuInfoDisplayConfig) {
          apiConfig = JSON.parse(res.data.response.MenuInfoDisplayConfig);
        }
      } catch (error) {
        console.warn('获取菜单配置失败，使用默认配置:', error);
      }

      // 辅助函数：根据后端配置获取某个模块的显隐状态
      const getVis = (category, name, def = false) => {
        if (!apiConfig) return def;
        const cat = apiConfig.find(c => c.title === category);
        if (!cat) return def;
        const item = cat.items.find(i => i.name === name);
        return item ? item.visible : def;
      };

      // 学校画像配置
      this.schoolTabNames = [
        { label: '组织架构', time: 15, visible: getVis('学校画像', '组织架构') },
        { label: '校园概况', time: 15, visible: getVis('学校画像', '校园概况') },
        { label: '校园风采', time: 15, visible: getVis('学校画像', '校园风采') }
      ];

      // 教师画像配置
      this.teacherTabNames = [
        { label: '教师概况', time: 15, visible: getVis('教师画像', '教师概况') },
        { label: '分级对比', time: 15, visible: getVis('教师画像', '分级对比') },
        { label: '名特优', time: 15, visible: getVis('教师画像', '名特优') },
        { label: '教师光荣榜', time: 15, visible: getVis('教师画像', '教师光荣榜') },
        { label: '教师形象墙', time: 15, visible: getVis('教师画像', '教师形象墙') }
      ];

      // 学生画像配置
      this.studentTabNames = [
        { label: '综合素质', time: 15, visible: getVis('学生画像', '综合素质', false) },
        { label: '点评榜单', time: 15, visible: getVis('学生画像', '点评榜单') },
        { label: '协约之星', time: 15, visible: getVis('学生画像', '协约之星') },
        { label: '学生笑脸墙', time: 15, visible: getVis('学生画像', '学生笑脸墙', false) }
      ];

      // 综合展示配置
      this.centerTabNames = [
        { label: '校园新风', time: 15, visible: getVis('综合展示', '校园新风') },
        { label: '校园动态', time: 15, visible: getVis('综合展示', '校园动态') },
        { label: '教师成长', time: 15, visible: getVis('综合展示', '教师成长') },
        { label: '学生成长', time: 15, visible: getVis('综合展示', '学生成长') }
      ];

      // 班级画像配置
      this.classTabNames = [
        { label: '班级风采', time: 15, visible: getVis('班级画像', '班级风采') },
        { label: '班级荣誉', time: 15, visible: getVis('班级画像', '班级荣誉') },
        { label: '值周评分', time: 15, visible: getVis('班级画像', '值周评分') }
      ];

      this.classCarouselSwitchTime = 5; //班徽,合照轮播间隔(秒)

      // 自动轮播时间配置 (秒)
      this.classSwitchTime = 10;        // 班级画像切换班级时间
      this.studentClassSwitchTime = 10; // 学生画像切换班级时间


      // ========== 从后端获取学校画像数据 ==========
      this.schoolOverviewStats = {
        totalTeachers: 0,
        totalStudents: 0,
        totalGrades: 0,
        totalClasses: 0,
        teacherStudentRatio: '',
        standardRatio: ''
      };
      this.schoolRadarData = [];
      this.schoolPhotoGallery = [];
      this.schoolOrgImage = '';

      try {
        const profileRes = await getSchoolProfileData({ departmentId: deptId });
        if (profileRes && profileRes.data && profileRes.data.success && profileRes.data.response) {
          const profile = profileRes.data.response;

          console.log(profile);
          this.organizationName = profile.OrganizationName || '';

          // 1. 组织架构图
          if (profile.OrganizationChart) {
            this.schoolOrgImage = this.$resolveImageSrc(profile.OrganizationChart);
          }

          // 2. 校园概况
          if (profile.Overview) {
            this.schoolOverviewStats = {
              totalTeachers: profile.Overview.TotalTeachers || 0,
              totalStudents: profile.Overview.TotalStudents || 0,
              totalGrades: profile.Overview.TotalGrades || 0,
              totalClasses: profile.Overview.TotalClasses || 0,
              teacherStudentRatio: profile.Overview.TeacherStudentRatio || '',
              standardRatio: profile.Overview.StandardRatio || '',
            };

            // 雷达图数据
            if (profile.Overview.GradeDistribution && profile.Overview.GradeDistribution.length > 0) {
              this.schoolRadarData = profile.Overview.GradeDistribution.map(g => ({
                name: g.Name,
                teacher: g.TeacherCount,
                student: g.StudentCount
              }));
            }
          }

          // 3. 校园风采
          if (profile.CampusSceneryList && profile.CampusSceneryList.length > 0) {
            this.schoolPhotoGallery = profile.CampusSceneryList.map(s => ({
              title: s.Title,
              image: s.Image ? this.$resolveImageSrc(s.Image) : ''
            }));
          }
        }
      } catch (error) {
        console.warn('获取学校画像数据失败:', error);
      }

      // ========== 从后端获取教师画像数据 ==========
      this.teacherOverviewData = { total: 0, male: 0, female: 0, grades: [], subjects: [] };
      this.famousTeachers = [];
      this.teacherClassificationData = { gender: [], title: [], education: [] };
      this.teacherAwardsData = { research: [], seniority: [] };

      try {
        const teacherRes = await getTeacherProfileData({ departmentId: deptId });
        if (teacherRes && teacherRes.data && teacherRes.data.success && teacherRes.data.response) {
          const tp = teacherRes.data.response;

          // 1. 教师概况
          if (tp.Overview) {
            this.teacherOverviewData = {
              total: tp.Overview.Total || 0,
              male: tp.Overview.Male || 0,
              female: tp.Overview.Female || 0,
              grades: (tp.Overview.TeachingAgeDistribution || []).map(d => ({ name: d.Name, value: d.Value })),
              subjects: (tp.Overview.SubjectDistribution || []).map(d => ({ name: d.Name, value: d.Value }))
            };
          }

          // 2. 分级对比
          if (tp.Classification) {
            this.teacherClassificationData = {
              gender: (tp.Classification.Gender || []).map(d => ({ name: d.Name, value: d.Value })),
              title: (tp.Classification.Title || []).map(d => ({ name: d.Name, value: d.Value })),
              education: (tp.Classification.Education || []).map(d => ({ name: d.Name, value: d.Value }))
            };
          }

          // 3. 名特优教师
          if (tp.FamousTeachers && tp.FamousTeachers.length > 0) {
            this.famousTeachers = tp.FamousTeachers.map(t => ({
              name: t.Name,
              title: t.Title,
              photo: t.Photo ? this.$resolveImageSrc(t.Photo) : '',
              departmentName: t.DepartmentName || ''
            }));
          } else {
            this.famousTeachers = [];
          }

          // 4. 教师光荣榜 (复用 famousTeachers 的结构，存到 honorTeachers)
          if (tp.HonorTeachers && tp.HonorTeachers.length > 0) {
            this.honorTeachers = tp.HonorTeachers.map(t => ({
              name: t.Name,
              title: t.Title,
              photo: t.Photo ? this.$resolveImageSrc(t.Photo) : '',
              departmentName: t.DepartmentName || ''
            }));
          }

          // 5. 教师形象墙
          if (tp.WallTeachers && tp.WallTeachers.length > 0) {
            this.wallTeachers = tp.WallTeachers.map(t => ({
              name: t.Name,
              title: t.Title,
              photo: t.Photo ? this.$resolveImageSrc(t.Photo) : '',
              departmentName: t.DepartmentName || ''
            }));
          }
        }
      } catch (error) {
        console.warn('获取教师画像数据失败:', error);
      }

      // ========== 从后端获取校园新风数据 ==========
      try {
        const freshWindRes = await getCampusFreshWindData({ departmentId: deptId });
        if (freshWindRes && freshWindRes.data && freshWindRes.data.success && freshWindRes.data.response) {
          const fw = freshWindRes.data.response;

          // 解析 SchoolImages (针对固定的分段 JSON 字符串数组格式优化)
          let parsedImages = [];
          if (fw.SchoolImages && fw.SchoolImages.length > 0) {
            try {
              // 按照真实格式，直接拼接所有字符串并替换缺失的逗号，然后进行 JSON 解析
              const combinedString = fw.SchoolImages.join('').replace(/\}\s*\{/g, '},{');
              const arr = JSON.parse(combinedString);
              if (Array.isArray(arr)) {
                parsedImages = arr.map(item => item.url || item);
              }
            } catch (e) {
              console.warn('解析 SchoolImages 失败:', e);
            }
          }

          // 去重并过滤非法值
          parsedImages = [...new Set(parsedImages.filter(img => typeof img === 'string' && img.length > 0))];

          this.campusFreshWindData = {
            carouselImages: parsedImages.length > 0
              ? parsedImages.map(img => {
                const path = img.trim();
                return this.$resolveImageSrc(path.startsWith('/') ? path : '/' + path);
              })
              : ['', '', ''],
            schoolIntro: fw.SchoolIntroduction || '',
            teachingBuildingCount: fw.TeachingBuildingCount || 0,
            aiChemistryLabCount: fw.AIChemistryLabCount || 0,
            scienceLabCount: fw.ScienceLabCount || 0,
            artSpaceCount: fw.ArtSpaceCount || 0,
            modelFarmCount: fw.ModelFarmCount || 0,
            stadiumCount: fw.StadiumCount || 0,
            lectureHallCount: fw.LectureHallCount || 0,
            cafeteriaCount: fw.CafeteriaCount || 0
          };
        } else {
          // 默认空数据或降级数据
          this.campusFreshWindData.carouselImages = ['', '', ''];
          this.campusFreshWindData.schoolIntro = '';
          this.campusFreshWindData.teachingBuildingCount = 0;
          this.campusFreshWindData.aiChemistryLabCount = 0;
          this.campusFreshWindData.scienceLabCount = 0;
          this.campusFreshWindData.artSpaceCount = 0;
          this.campusFreshWindData.modelFarmCount = 0;
          this.campusFreshWindData.stadiumCount = 0;
          this.campusFreshWindData.lectureHallCount = 0;
          this.campusFreshWindData.cafeteriaCount = 0;
        }
      } catch (error) {
        console.warn('获取校园新风数据失败:', error);
      }

      this.studentDataSets = [
        [
          { name: '张小凡', class: '高一(2)班', rating: 5, score: 98 },
          { name: '林惊羽', class: '高一(1)班', rating: 5, score: 97 },
          { name: '陆雪琪', class: '高二(3)班', rating: 5, score: 96 },
          { name: '碧瑶', class: '高二(1)班', rating: 4, score: 94 },
          { name: '曾书书', class: '高一(5)班', rating: 4, score: 92 },
        ],
        [
          { name: '飞鹰小组', class: '高一(2)班', rating: 5, score: 950 },
          { name: '雄狮小组', class: '高二(1)班', rating: 5, score: 920 },
          { name: '火箭小组', class: '高三(4)班', rating: 4, score: 880 },
          { name: '梦想小组', class: '高一(1)班', rating: 4, score: 850 },
          { name: '奋斗小组', class: '高二(2)班', rating: 3, score: 810 },
        ],
        [
          { name: '王大锤', class: '高二(6)班', rating: 5, score: '体育' },
          { name: '李狗蛋', class: '高一(3)班', rating: 5, score: '美术' },
          { name: '赵铁柱', class: '高三(1)班', rating: 4, score: '音乐' },
          { name: '刘波', class: '高一(2)班', rating: 4, score: '编程' },
          { name: '龙傲天', class: '高二(1)班', rating: 4, score: '写作' },
        ]
      ];

      try {
        const classRes = await getBigScreenClasses({ departmentId: deptId });
        if (classRes && classRes.data && classRes.data.success && classRes.data.response) {
          const rawGroups = classRes.data.response;
          // 转换为 el-cascader 格式
          this.classSelectOptions = rawGroups.map(g => ({
            value: g.ParentId + '',
            label: g.ParentName,
            children: (g.Children || []).map(c => ({
              value: c.Id + '',
              label: c.Name
            }))
          }));

          // 如果有数据，初始化默认选中项
          if (this.classSelectOptions.length > 0 && this.classSelectOptions[0].children.length > 0) {
            const firstGrade = this.classSelectOptions[0];
            const firstClass = firstGrade.children[0];
            this.selectedClass = [firstGrade.value, firstClass.value];
            this.selectedStudentClass = [firstGrade.value, firstClass.value];
          }
        }
      } catch (error) {
        console.warn('获取班级数据失败:', error);
      }

      this.classProfileData = {
        teacherName: '暂无设置',
        teacherRole: '班主任',
        teacherPhoto: '',
        teacherMessage: '欢迎来到本班级！',
        classBadge: '',
        classGroupPhoto: ''
      };

      this.studentDataMap = {
        'grade1_class1': {
          evaluation: {
            scoreList: [
              { rank: 1, name: '许瀚文', score: 23 },
              { rank: 2, name: '郑好', score: 22 },
              { rank: 3, name: '张梓林', score: 21 },
              { rank: 4, name: '张震元', score: 21 },
              { rank: 5, name: '于晨璐', score: 20 },
              { rank: 6, name: '徐可歆', score: 20 },
              { rank: 7, name: '谢芯贝', score: 20 },
              { rank: 8, name: '金雨涵', score: 20 }
            ],
            singleItemList: [
              {
                itemName: '文明劝阻有实效',
                score: 90,
                children: [
                  { rank: 1, name: '陈俊宇', score: 2 },
                  { rank: 2, name: '陈童子墨', score: 2 },
                  { rank: 3, name: '戴上', score: 2 }
                ]
              },
              {
                itemName: '上课认真',
                score: 73,
                children: [
                  { rank: 1, name: '张震元', score: 3 },
                  { rank: 2, name: '葛陈霞', score: 2 },
                  { rank: 3, name: '单芯羽', score: 2 }
                ]
              }
            ]
          },
          stars: [
            { rank: 9, name: '马俊毅', score: 98 },
            { rank: 10, name: '洋博晨', score: 98 },
            { rank: 11, name: '王凌誉', score: 97 },
            { rank: 12, name: '王刷丞', score: 97 },
            { rank: 13, name: '许瀚文', score: 96 },
            { rank: 14, name: '朱宣烨', score: 96 },
            { rank: 15, name: '朱昱安', score: 95 },
            { rank: 16, name: '李晓明', score: 94 },
            { rank: 17, name: '张伟', score: 93 }
          ]
        },
        'grade1_class2': {
          evaluation: {
            scoreList: [
              { rank: 1, name: '李小白', score: 25 },
              { rank: 2, name: '杜甫', score: 24 },
              { rank: 3, name: '白居易', score: 23 },
              { rank: 4, name: '王维', score: 22 }
            ],
            singleItemList: [
              {
                itemName: '乐于助人',
                score: 85,
                children: [
                  { rank: 1, name: '李小白', score: 5 },
                  { rank: 2, name: '杜甫', score: 4 }
                ]
              }
            ]
          },
          stars: [
            { rank: 1, name: '李小白', score: 99 },
            { rank: 2, name: '杜甫', score: 98 }
          ]
        },
        'grade2_class1': {
          evaluation: {
            scoreList: [
              { rank: 1, name: '孙悟空', score: 30 },
              { rank: 2, name: '猪八戒', score: 29 },
              { rank: 3, name: '沙和尚', score: 28 },
              { rank: 4, name: '唐僧', score: 27 }
            ],
            singleItemList: [
              {
                itemName: '取经小分队',
                score: 100,
                children: [
                  { rank: 1, name: '孙悟空', score: 10 },
                  { rank: 2, name: '猪八戒', score: 8 }
                ]
              }
            ]
          },
          stars: [
            { rank: 1, name: '孙悟空', score: 100 },
            { rank: 2, name: '唐僧', score: 99 }
          ]
        }
      };

      this.schoolIntro = '临海市哲商现代实验小学创办于2002年 6月，学校以辛亥革命烈士杨哲商之名命名。2022年8月，应临海市教育局深化教育改革，激活教育资源需要，哲商现代实验小学与托阳小学实现融合办学，实施“一校两区”办学模式改革，开启了哲商现代实验小学名校集团化发展的新篇章，2023年8月西校区搬迁至大洋中路新校园。学校本着“西校办精，东校做强”的发展策略，奉行“让每一个孩子在今天的成功愉悦中走向明天”的办学宗旨，秉持“童心有约，悦动哲商”为办学理念，以“我的协约最美”为校训，积极打造协约式教育，历年教育教学质量名列全市前茅。';

      this.navButtons = [
        { label: 'AI实验室', icon: 'fa fa-microchip' }, { label: '滕浩', icon: 'fa fa-book' },
        { label: '智能操场', icon: 'fa fa-soccer-ball-o' }, { label: '智慧食堂', icon: 'fa fa-cutlery' },
        { label: '平安校园', icon: 'fa fa-shield' }, { label: '协同办公', icon: 'fa fa-briefcase' }
      ];

      this.carouselImages = ['', '', ''];
    },

    handleResize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const ratio = w / h;
      const targetRatio = this.width / this.height; // 3600 / 1080 = 3.33

      // Threshold: if screen is significantly narrower than target (e.g. < 3.0), enter scroll mode
      if (ratio < targetRatio * 0.9) {
        this.isScrollMode = true;
        this.scale = h / this.height; // Scale to fit height
      } else {
        this.isScrollMode = false;
        const scaleX = w / this.width;
        const scaleY = h / this.height;
        this.scale = Math.min(scaleX, scaleY);
      }
    },

    // --- 统一时间与事件驱动系统 ---

    formateDate(fmt = 'yyyy-mm-dd', date = '') {
      let targetDate;
      if (!date) {
        targetDate = new Date();
      } else if (typeof date === 'string') {
        try {
          const parsedDate = Date.parse(date.replace(/-/g, '/').replace(/T/g, ' '));
          targetDate = isNaN(parsedDate) ? new Date() : new Date(parsedDate);
        } catch (error) {
          console.log('时间格式化出错', error);
          targetDate = new Date();
        }
      } else if (typeof date === 'number') {
        targetDate = new Date(date);
      } else {
        targetDate = date;
      }

      const year = targetDate.getFullYear();
      const month = targetDate.getMonth() + 1;
      const day = targetDate.getDate();
      const hours = targetDate.getHours();
      const minutes = targetDate.getMinutes();
      const seconds = targetDate.getSeconds();
      const dayOfWeek = targetDate.getDay();

      const weekArrTxt = ['日', '一', '二', '三', '四', '五', '六'];
      const weekArrTxt2 = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      return fmt
        .replace('yyyy', year)
        .replace('mm', month.toString().padStart(2, '0'))
        .replace('dd', day.toString().padStart(2, '0'))
        .replace('HH', hours.toString().padStart(2, '0'))
        .replace('MM', minutes.toString().padStart(2, '0'))
        .replace('SS', seconds.toString().padStart(2, '0'))
        .replace('y', year.toString())
        .replace('m', month.toString())
        .replace('d', day.toString())
        .replace('H', hours.toString())
        .replace('M', minutes.toString())
        .replace('S', seconds.toString())
        .replace('W', weekArrTxt[dayOfWeek])
        .replace('w', weekArrTxt2[dayOfWeek]);
    },

    addEvent(eventName, time) {
      // 获取当前时间 + time 秒的时间 (格式 dHHMMSS)
      const targetTime = this.formateDate(
        'dHHMMSS',
        new Date(new Date().getTime() + time * 1000)
      );
      this.eventFunctions.set(eventName, parseInt(targetTime));
    },

    consumeEvent(timeStr) {
      const time = parseInt(this.formateDate('dHHMMSS', timeStr));

      this.eventFunctions.forEach((value, key) => {
        if (time >= value) {
          try {
            this.eventFunctions.delete(key);
            // 触发对应方法
            if (this[key] && typeof this[key] === 'function') {
              this[key]();
            }
          } catch (error) {
            console.log(error);
          }
        }
      });
    },

    startTimeSync() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }

      // 初始时间
      let nowTime = this.formateDate("yyyy-mm-dd HH:MM:SS");
      this.currentTime = nowTime;
      this.systemTime = nowTime;

      this.timer = setInterval(() => {
        let time = this.formateDate("yyyy-mm-dd HH:MM:SS");
        this.currentTime = time;
        this.systemTime = time;

        // 消费事件
        this.consumeEvent(time);
      }, 1000);
    },

    // 模块自动轮播 (由事件驱动) - 拆分为独立控制
    scheduleNextRotation(type) {
      // type: 'School', 'Teacher', 'Student'
      const visibleTabs = this[`visible${type}Tabs`];
      if (!visibleTabs || visibleTabs.length === 0) return;

      const currentIndex = this[`current${type}Tab`];
      // 确保索引在有效范围内
      const safeIndex = currentIndex % visibleTabs.length;
      const currentTab = visibleTabs[safeIndex];

      // 获取当前 tab 配置的停留时间，默认 5 秒
      const duration = (currentTab && currentTab.time) ? currentTab.time : 5;

      this.addEvent(`rotate${type}`, duration);
    },

    rotateSchool() {
      if (this.visibleSchoolTabs.length === 0) return;
      this.currentSchoolTab = (this.currentSchoolTab + 1) % this.visibleSchoolTabs.length;
      this.scheduleNextRotation('School');
    },

    rotateTeacher() {
      if (this.visibleTeacherTabs.length === 0) return;
      this.currentTeacherTab = (this.currentTeacherTab + 1) % this.visibleTeacherTabs.length;
      this.scheduleNextRotation('Teacher');
    },

    rotateStudent() {
      if (this.visibleStudentTabs.length === 0) return;
      this.currentStudentTab = (this.currentStudentTab + 1) % this.visibleStudentTabs.length;
      this.scheduleNextRotation('Student');
    },

    rotateCenter() {
      if (this.visibleCenterTabs.length === 0) return;
      this.currentCenterTab = (this.currentCenterTab + 1) % this.visibleCenterTabs.length;
      this.scheduleNextRotation('Center');
    },

    handleCenterTabClick(index) {
      if (this.currentCenterTab === index) return;
      this.currentCenterTab = index;
      this.eventFunctions.delete('rotateCenter');
      this.scheduleNextRotation('Center');
    },

    handleStudentTabClick(index) {
      if (this.currentStudentTab === index) return;

      this.currentStudentTab = index;

      // Reset rotation timer to prevent immediate auto-switch
      this.eventFunctions.delete('rotateStudent');
      this.scheduleNextRotation('Student');
    },

    handleClassTabClick(index) {
      if (this.currentClassTab === index) return;
      this.currentClassTab = index;
      this.eventFunctions.delete('rotateClassTab');
      this.scheduleNextRotation('Class');
    },

    rotateClassTab() {
      if (!this.visibleClassTabs || this.visibleClassTabs.length === 0) return;
      this.currentClassTab = (this.currentClassTab + 1) % this.visibleClassTabs.length;
      this.scheduleNextRotation('Class');
    },

    // --- 班级自动轮播逻辑 ---
    handleClassChange(value) {
      if (value && value.length >= 2) {
        this.selectedClass = value;
        this.reloadClassTabData();

        // 重置轮播计时
        this.eventFunctions.delete('rotateClass');
        this.addEvent('rotateClass', this.classSwitchTime || 10);
      }
    },
    // 重新加载班级 Tab 数据（班级画像核心）
    async reloadClassTabData() {
      if (!this.selectedClass || this.selectedClass.length < 2) return;
      const classId = this.selectedClass[1];

      try {
        const res = await getBigScreenClassProfile({ departmentId: classId });
        if (res && res.data && res.data.success && res.data.response) {
          const info = res.data.response;
          const ext = info.ClassExtensionInfo || {};

          // 处理图片路径前缀
          const formatUrl = (url) => {
            if (!url) return '';
            if (url.startsWith('http') || url.startsWith('https') || url.startsWith('data:')) return url;
            const baseUrl = BaseApiUrl.endsWith('/') ? BaseApiUrl : BaseApiUrl + '/';
            const relativeUrl = url.startsWith('/') ? url.substring(1) : url;
            return baseUrl + relativeUrl;
          };

          this.classProfileData = {
            teacherName: info.ClassTeacherName || '暂未设置',
            teacherRole: '班主任',
            teacherPhoto: formatUrl(ext.Logo),
            teacherMessage: ext.HeadTeacherMessage || '欢迎来到本班级！',
            classBadge: formatUrl(ext.ClassLogo),
            classGroupPhoto: '' // 暂无班级合照字段，保留为空
          };
        } else {
          // 降级显示
          this.classProfileData = {
            teacherName: '暂未设置',
            teacherRole: '班主任',
            teacherPhoto: '',
            teacherMessage: '欢迎来到本班级！',
            classBadge: '',
            classGroupPhoto: ''
          };
        }
      } catch (error) {
        console.warn('获取班级画像信息失败:', error);
      }
    },

    onClassGradeChanged(ids) {
      if (ids && ids.length >= 2) {
        this.selectedClass = ids;
        this.reloadClassTabData();
        // 重置轮播计时
        this.eventFunctions.delete('rotateClass');
        this.addEvent('rotateClass', this.classSwitchTime || 10);
      }
    },

    rotateClass() {
      if (!this.selectedClass || this.selectedClass.length < 2) return;

      const currentGrade = this.selectedClass[0];
      const currentClass = this.selectedClass[1];

      let gradeIndex = this.classSelectOptions.findIndex(g => g.value === currentGrade);
      if (gradeIndex === -1) gradeIndex = 0;

      const grade = this.classSelectOptions[gradeIndex];
      let classIndex = grade.children.findIndex(c => c.value === currentClass);

      // 切换到下一个班级
      classIndex++;

      // 如果当前年级班级遍历完，切换到下一个年级
      if (classIndex >= grade.children.length) {
        classIndex = 0;
        gradeIndex++;
        // 如果年级遍历完，回到第一个年级
        if (gradeIndex >= this.classSelectOptions.length) {
          gradeIndex = 0;
        }
      }

      const nextGrade = this.classSelectOptions[gradeIndex];
      const nextClass = nextGrade.children[classIndex];

      this.selectedClass = [nextGrade.value, nextClass.value];
      this.reloadClassTabData();

      // 注册下一次轮播
      this.addEvent('rotateClass', this.classSwitchTime || 10);
    },

    // --- 学生画像逻辑 ---

    reloadStudentTabData() {
      if (!this.selectedStudentClass || this.selectedStudentClass.length < 2) return;

      const [gradeId, classId] = this.selectedStudentClass;
      const classKey = `${gradeId}_${classId}`;

      console.log('Reloading student data for:', classKey);

      let originalData = this.studentDataMap[classKey];

      // If no data found for this class, use empty structure instead of fallback
      // This ensures we don't confusingly show Grade 1 Class 1 data for everything
      if (!originalData) {
        originalData = {
          evaluation: { scoreList: [], singleItemList: [] },
          stars: []
        };
      }

      // Reset to original data (clears previous duplication)
      if (originalData) {
        this.studentProfileData = JSON.parse(JSON.stringify(originalData));
      }

      // Reset scroll positions
      this.scrollPositions.score = 0;
      this.scrollPositions.single = 0;
      this.scrollPositions.stars = 0;

      // Check height and duplicate if needed for the ACTIVE tab
      this.$nextTick(() => {
        // Tab 0: 点评榜单
        if (this.currentStudentTab === 0) {
          this.checkAndDuplicateList('scoreList', 'evaluation.scoreList');
          this.checkAndDuplicateList('singleList', 'evaluation.singleItemList');
        }
        // Tab 1: 协约之星
        else if (this.currentStudentTab === 1) {
          this.checkAndDuplicateList('starList', 'stars');
        }
      });
    },

    reloadSchoolTabData() {
      // Only for "校园风采" tab (School Gallery) which needs scrolling
      if (this.visibleSchoolTabs[this.currentSchoolTab] && this.visibleSchoolTabs[this.currentSchoolTab].label === '校园风采') {
        // Reset to original data
        if (this.originalSchoolPhotoGallery) {
          this.schoolPhotoGallery = [...this.originalSchoolPhotoGallery];
        }
        this.scrollPositions.school = 0;

        this.$nextTick(() => {
          this.checkAndDuplicateList('schoolGallery', 'schoolPhotoGallery', this);
        });
      }
    },

    reloadTeacherTabData() {
      // Reset to original arrays when switching teacher tabs and prepare scrolling duplication
      if (this.originalFamousTeachers) this.famousTeachers = [...this.originalFamousTeachers];
      if (this.originalHonorTeachers) this.honorTeachers = [...this.originalHonorTeachers];
      if (this.originalWallTeachers) this.wallTeachers = [...this.originalWallTeachers];

      this.scrollPositions.famous = 0;
      this.scrollPositions.honor = 0;
      this.scrollPositions.wall = 0;

      this.$nextTick(() => {
        this.checkAndDuplicateList('famousTeacherList', 'famousTeachers', this);
        this.checkAndDuplicateList('honorTeacherList', 'honorTeachers', this);
        this.checkAndDuplicateList('wallTeacherList', 'wallTeachers', this);
      });
    },

    handleStudentClassChange(value) {
      if (value && value.length >= 2) {
        this.selectedStudentClass = value;

        // Defer data reload to avoid UI stutter/scroll jumps during Cascader close
        setTimeout(() => {
          this.reloadStudentTabData();
        }, 100);

        // Reset rotation timer
        this.eventFunctions.delete('rotateStudentClass');
        this.addEvent('rotateStudentClass', this.studentClassSwitchTime || 10);
      }
    },

    checkAndDuplicateList(refName, dataPath, root = this.studentProfileData) {
      const el = this.$refs[refName];
      if (!el) return;

      // Measure content height vs container height
      // Note: data is currently non-duplicated.
      const containerHeight = el.clientHeight;
      const contentHeight = el.scrollHeight;

      if (contentHeight > containerHeight) {
        // Need to duplicate to allow seamless scrolling
        // Access data by path string
        const keys = dataPath.split('.');
        let target = root;
        for (let i = 0; i < keys.length - 1; i++) {
          target = target[keys[i]];
        }
        const lastKey = keys[keys.length - 1];
        const originalList = target[lastKey];

        if (originalList && Array.isArray(originalList)) {
          // Duplicate the list
          target[lastKey] = [...originalList, ...originalList];
        }
      }
    },

    rotateStudentClass() {
      if (!this.selectedStudentClass || this.selectedStudentClass.length < 2) return;

      const currentGrade = this.selectedStudentClass[0];
      const currentClass = this.selectedStudentClass[1];

      let gradeIndex = this.studentSelectOptions.findIndex(g => g.value === currentGrade);
      if (gradeIndex === -1) gradeIndex = 0;

      const grade = this.studentSelectOptions[gradeIndex];
      let classIndex = grade.children.findIndex(c => c.value === currentClass);

      classIndex++;

      if (classIndex >= grade.children.length) {
        classIndex = 0;
        gradeIndex++;
        if (gradeIndex >= this.studentSelectOptions.length) {
          gradeIndex = 0;
        }
      }

      const nextGrade = this.studentSelectOptions[gradeIndex];
      const nextClass = nextGrade.children[classIndex];

      this.handleStudentClassChange([nextGrade.value, nextClass.value]);
    },

    startAutoScroll() {
      if (this.scrollTimer) clearInterval(this.scrollTimer);
      // Revert to 20ms for smooth continuous scrolling
      this.scrollTimer = setInterval(this.autoScroll, 20);
    },

    autoScroll() {
      if (!this.isScrolling) return;

      if (this.$refs.scoreList) this.scrollElement(this.$refs.scoreList, 'score');
      if (this.$refs.singleList) this.scrollElement(this.$refs.singleList, 'single');
      if (this.$refs.starList) this.scrollElement(this.$refs.starList, 'stars');
      if (this.$refs.schoolGallery) this.scrollElement(this.$refs.schoolGallery, 'school');
      if (this.$refs.famousTeacherList) this.scrollElement(this.$refs.famousTeacherList, 'famous');
      if (this.$refs.honorTeacherList) this.scrollElement(this.$refs.honorTeacherList, 'honor');
      if (this.$refs.wallTeacherList) this.scrollElement(this.$refs.wallTeacherList, 'wall');
    },

    scrollElement(el, key) {
      // If content fits, no scroll
      if (el.scrollHeight <= el.clientHeight) {
        this.scrollPositions[key] = 0;
        el.scrollTop = 0;
        return;
      }

      // Continuous scroll logic
      this.scrollPositions[key] += 0.5;

      const halfHeight = el.scrollHeight / 2;

      if (this.scrollPositions[key] >= halfHeight) {
        this.scrollPositions[key] = 0;
      }

      el.scrollTop = this.scrollPositions[key];
    },

    handleClassCarouselChange(index) {
      this.currentClassCarouselIndex = index;
    },

    switchClassCarousel(index) {
      this.$refs.classCarousel.setActiveItem(index);
    },

    // --- 原有业务逻辑保持不变 ---

    handleSchoolTabClick(index) {
      if (this.currentSchoolTab === index) return;
      this.currentSchoolTab = index;
      this.eventFunctions.delete('rotateSchool');
      this.scheduleNextRotation('School');
    },

    handleTeacherTabClick(index) {
      if (this.currentTeacherTab === index) return;
      this.currentTeacherTab = index;
      // 重置轮播: 移除原有计划，立即注册一个新的
      this.eventFunctions.delete('rotateTeacher');
      this.scheduleNextRotation('Teacher');
    },
    handleTeacherDotHover(index) {
      this.hoverTeacherTab = index;
    },
    handleTeacherDotLeave() {
      this.hoverTeacherTab = -1;
    },

    // --- 学校画像图表 ---
    initSchoolRadarChart() {
      this.$nextTick(() => {
        if (this.currentSchoolTab !== 1) return;

        const el = this.$refs.schoolRadarChart;
        if (!el) return;

        if (this.charts.s_radar) {
          this.charts.s_radar.dispose();
        }
        this.charts.s_radar = echarts.init(el);
        this.updateSchoolRadarChart();
      });
    },

    updateSchoolRadarChart() {
      if (!this.charts.s_radar) return;

      // 动态计算max值
      const maxStudent = Math.max(...this.schoolRadarData.map(item => item.student), 100);
      const maxTeacher = Math.max(...this.schoolRadarData.map(item => item.teacher * 10), 100);
      const maxVal = Math.max(maxStudent, maxTeacher) * 1.2; // 留20%余量

      // 根据年级数量调整雷达图大小和字体
      const gradeCount = this.schoolRadarData.length;
      const radarRadius = gradeCount > 10 ? '40%' : gradeCount > 6 ? '48%' : '55%';
      const fontSize = gradeCount > 10 ? 10 : gradeCount > 6 ? 11 : 12;

      const indicators = this.schoolRadarData.map(item => ({
        name: item.name,
        max: Math.ceil(maxVal / 100) * 100
      }));
      const teacherData = this.schoolRadarData.map(item => item.teacher * 10); // 放大教师数据以便展示
      const studentData = this.schoolRadarData.map(item => item.student);

      const option = {
        title: {
          text: '教师学生分布',
          left: 'center',
          top: '5%',
          textStyle: { color: '#fff', fontSize: 16, fontFamily: 'YouSheBiaoTiHei' }
        },
        tooltip: {
          confine: true, // 限制在容器内
          extraCssText: 'max-height: 300px; overflow-y: auto; white-space: normal;'
        },
        legend: {
          data: ['教师分布', '学生分布'],
          bottom: '5%',
          textStyle: { color: '#fff' }
        },
        radar: {
          indicator: indicators,
          radius: radarRadius,
          center: ['50%', '50%'],
          axisName: {
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: fontSize,
            formatter: function (value) {
              // 超过6个字符换行显示
              if (value.length > 6) {
                return value.substring(0, 6) + '\n' + value.substring(6);
              }
              return value;
            }
          },
          splitArea: {
            areaStyle: {
              color: ['rgba(0, 242, 255, 0.1)', 'rgba(0, 0, 0, 0)']
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 242, 255, 0.2)'
            }
          }
        },
        series: [{
          name: '教师 vs 学生',
          type: 'radar',
          data: [
            {
              value: teacherData,
              name: '教师分布',
              itemStyle: { color: '#5c00ff' },
              areaStyle: { opacity: 0.3 }
            },
            {
              value: studentData,
              name: '学生分布',
              itemStyle: { color: '#00f2ff' },
              areaStyle: { opacity: 0.3 }
            }
          ]
        }]
      };

      this.charts.s_radar.setOption(option, true);
    },

    getRankColor(index) {
      return ['#ffd700', '#c0c0c0', '#cd7f32'][index] || '#fff';
    },
    initTeacherChart() {
      this.$nextTick(() => {
        if (this.currentTeacherTab !== 0) return;

        const refs = ['teacherChart1', 'teacherChart2'];
        const keys = ['t1', 't2'];

        refs.forEach((ref, index) => {
          const el = this.$refs[ref];
          const key = keys[index];

          if (!el) return;

          if (this.charts[key]) {
            this.charts[key].dispose();
          }
          this.charts[key] = echarts.init(el);
        });

        this.updateTeacherChart();
      });
    },
    disposeCharts() {
      ['main', 't1', 't2', 'c1', 'c2', 'c3', 'a1', 'a2', 's_radar'].forEach(k => {
        if (this.charts[k]) {
          this.charts[k].dispose();
          this.charts[k] = null;
        }
      });
    },
    updateTeacherChart() {
      if (this.currentTeacherTab !== 0) return;

      // 统一配色
      var pieColors = ['#00f2ff', '#0078ff', '#5c6eff', '#a45cff', '#e052cf', '#ff6b6b', '#ffb347', '#47d1a0', '#36a2eb', '#9966ff', '#ff9f7f', '#67c23a'];

      // t1: 教师任教年级分布 (横向柱状图)
      if (this.charts.t1) {
        var option1 = {
          title: {
            text: '教师任教年级分布',
            left: 'center',
            top: '5%',
            textStyle: { color: '#fff', fontSize: 16, fontWeight: 'normal', fontFamily: 'YouSheBiaoTiHei' }
          },
          grid: { left: '15%', right: '10%', top: '20%', bottom: '10%' },
          xAxis: {
            type: 'value',
            axisLine: { lineStyle: { color: 'rgba(0, 242, 255, 0.3)' } },
            axisLabel: { color: 'rgba(255, 255, 255, 0.6)' },
            splitLine: { lineStyle: { color: 'rgba(0, 242, 255, 0.1)' } }
          },
          yAxis: {
            type: 'category',
            data: this.teacherOverviewData.grades.map(function (item) { return item.name; }),
            axisLine: { lineStyle: { color: 'rgba(0, 242, 255, 0.3)' } },
            axisLabel: { color: '#fff', fontSize: 14 }
          },
          series: [{
            type: 'bar',
            data: this.teacherOverviewData.grades.map(function (item) { return item.value; }),
            itemStyle: {
              normal: {
                color: new echarts.graphic.LinearGradient(1, 0, 0, 0, [
                  { offset: 0, color: '#00f2ff' },
                  { offset: 1, color: '#0055ff' }
                ]),
                barBorderRadius: [0, 4, 4, 0]
              }
            },
            barWidth: '50%',
            label: { show: true, position: 'right', color: '#00f2ff', fontSize: 12 },
            animationDuration: 1000
          }]
        };
        this.charts.t1.setOption(option1, true);
      }

      // t2: 教师学科分布 (环形图)
      if (this.charts.t2) {
        var subjectData = [];
        for (var i = 0; i < this.teacherOverviewData.subjects.length; i++) {
          var d = this.teacherOverviewData.subjects[i];
          subjectData.push({
            name: d.name,
            value: d.value,
            itemStyle: { normal: { color: pieColors[i % pieColors.length] } }
          });
        }
        var option2 = {
          color: pieColors,
          title: {
            text: '教师学科分布',
            left: 'center',
            top: '5%',
            textStyle: { color: '#fff', fontSize: 16, fontWeight: 'normal', fontFamily: 'YouSheBiaoTiHei' }
          },
          tooltip: { trigger: 'item' },
          series: [{
            type: 'pie',
            radius: ['30%', '55%'],
            center: ['50%', '55%'],
            itemStyle: {
              normal: {
                borderColor: '#03051e',
                borderWidth: 2
              }
            },
            label: {
              show: true,
              position: 'outside',
              formatter: '{b}\n{d}%',
              color: '#fff',
              fontSize: 11
            },
            labelLine: {
              length: 10,
              length2: 8
            },
            data: subjectData,
            animationDuration: 1000
          }]
        };
        this.charts.t2.setOption(option2, true);
      }
    },

    // 分级对比图表方法 (更新后的版本 - 3个环形图)
    initClassificationCharts() {
      this.$nextTick(() => {
        if (this.currentTeacherTab !== 1) return;

        const refs = ['teacherClassChart1', 'teacherClassChart2', 'teacherClassChart3'];
        const keys = ['c1', 'c2', 'c3'];

        refs.forEach((ref, index) => {
          const el = this.$refs[ref];
          const key = keys[index];

          if (!el) return;

          if (this.charts[key]) {
            this.charts[key].dispose();
          }
          this.charts[key] = echarts.init(el);
        });

        this.updateClassificationCharts();
      });
    },

    updateClassificationCharts() {
      if (this.currentTeacherTab !== 1) return;

      // 统一配色
      var genderColors = ['#36a2eb', '#e052cf'];
      var titleColors = ['#00f2ff', '#5c6eff', '#a45cff', '#e052cf', '#ff6b6b', '#ffb347', '#36a2eb', '#47d1a0'];
      var eduColors = ['#36a2eb', '#a45cff', '#47d1a0', '#ffb347', '#e052cf', '#00f2ff', '#ff6b6b', '#9966ff'];

      // c1: 教职工性别分布
      if (this.charts.c1) {
        var genderData = [];
        for (var i = 0; i < this.teacherClassificationData.gender.length; i++) {
          var g = this.teacherClassificationData.gender[i];
          genderData.push({ name: g.name, value: g.value, itemStyle: { normal: { color: genderColors[i % genderColors.length] } } });
        }
        this.charts.c1.setOption({
          color: genderColors,
          title: { text: '教职工性别分布', left: 'center', top: '5%', textStyle: { color: '#fff', fontSize: 16, fontWeight: 'normal', fontFamily: 'YouSheBiaoTiHei' } },
          tooltip: { trigger: 'item' },
          series: [{
            type: 'pie', radius: ['30%', '55%'], center: ['50%', '55%'],
            itemStyle: { normal: { borderColor: '#03051e', borderWidth: 2 } },
            label: { show: true, position: 'outside', formatter: '{b}\n{d}%', color: '#fff', fontSize: 12 },
            labelLine: { length: 10, length2: 8 },
            data: genderData
          }]
        }, true);
      }

      // c2: 教职工职称分布
      if (this.charts.c2) {
        var titleData = [];
        for (var j = 0; j < this.teacherClassificationData.title.length; j++) {
          var t = this.teacherClassificationData.title[j];
          titleData.push({ name: t.name, value: t.value, itemStyle: { normal: { color: titleColors[j % titleColors.length] } } });
        }
        this.charts.c2.setOption({
          color: titleColors,
          title: { text: '教职工职称分布', left: 'center', top: '5%', textStyle: { color: '#fff', fontSize: 16, fontWeight: 'normal', fontFamily: 'YouSheBiaoTiHei' } },
          tooltip: { trigger: 'item' },
          series: [{
            type: 'pie', radius: ['30%', '55%'], center: ['50%', '55%'],
            itemStyle: { normal: { borderColor: '#03051e', borderWidth: 2 } },
            label: { show: true, position: 'outside', formatter: '{b}\n{d}%', color: '#fff', fontSize: 12 },
            labelLine: { length: 10, length2: 8 },
            data: titleData
          }]
        }, true);
      }

      // c3: 教职工学历分布
      if (this.charts.c3) {
        var eduData = [];
        for (var k = 0; k < this.teacherClassificationData.education.length; k++) {
          var e = this.teacherClassificationData.education[k];
          eduData.push({ name: e.name, value: e.value, itemStyle: { normal: { color: eduColors[k % eduColors.length] } } });
        }
        this.charts.c3.setOption({
          color: eduColors,
          title: { text: '教职工学历分布', left: 'center', top: '5%', textStyle: { color: '#fff', fontSize: 16, fontWeight: 'normal', fontFamily: 'YouSheBiaoTiHei' } },
          tooltip: { trigger: 'item' },
          series: [{
            type: 'pie', radius: ['30%', '55%'], center: ['50%', '55%'],
            itemStyle: { normal: { borderColor: '#03051e', borderWidth: 2 } },
            label: { show: true, position: 'outside', formatter: '{b}\n{d}%', color: '#fff', fontSize: 12 },
            labelLine: { length: 10, length2: 8 },
            data: eduData
          }]
        }, true);
      }
    },

  }
}
</script>

<style lang="stylus" scoped>
@font-face
  font-family 'YouSheBiaoTiHei'
  src url('https://minio.zj-caiyu.com/secp/font/YouSheBiaoTiHei.ttf') format('truetype')

.screen-wrapper
  width 100vw
  height 100vh
  background-color #03051e
  overflow hidden
  position relative
  // 移除错误的全局鼠标手型
  
  &.scroll-mode
    overflow-x auto
    overflow-y hidden
    display flex
    align-items flex-start // Align top
    justify-content flex-start
    
    // Custom scrollbar
    &::-webkit-scrollbar
      height 12px
      background rgba(0,0,0,0.3)
      // 在轨道或滑块上时鼠标变为手形
      cursor pointer
      
    &::-webkit-scrollbar-thumb
      background rgba(0, 242, 255, 0.6)
      border-radius 6px
      border 2px solid transparent
      background-clip content-box
      cursor pointer // 确保滑块上有手形
      &:hover
        background rgba(0, 242, 255, 0.9)

  .screen-scaler
    transition all 0.3s ease

// 双图表容器 (分级对比、著作奖项)
.chart-dual-container
  display flex
  justify-content space-between
  align-items center
  height 100%
  width 100%
  padding 0 10px
  gap 20px
  
  .chart-half-wrapper
    flex 1
    height 100%
    position relative
    
    .photo-item
      position relative
      background linear-gradient(180deg, rgba(3,12,35,0.9), rgba(2,8,30,0.88))
      border-radius 12px
      border 1px solid rgba(0, 242, 255, 0.12)
      box-shadow 0 10px 30px rgba(0,0,0,0.65), 0 0 20px rgba(0, 242, 255, 0.04) inset
      display flex
      flex-direction column
      align-items center
      justify-content center
      padding 18px
      box-sizing border-box

      &::before
        content ''
        position absolute
        top 6px
        left 6px
        right 6px
        bottom 6px
        border-radius 10px
        border 1px solid rgba(0, 242, 255, 0.06)
        box-shadow 0 0 18px rgba(0, 242, 255, 0.12)
        pointer-events none

// 大屏全局容器
.screen-container
  height 1080px !important
  width 3600px !important
  position relative
  background-color #03051e
  background-image radial-gradient(circle at 50% 50%, #0a113a 0%, #03051e 100%)
  display flex
  flex-direction column
  overflow hidden
  z-index 1
  box-sizing border-box

  // 大屏顶栏
  .screen-header
    height 85px
    display flex
    justify-content space-between
    align-items center
    padding 0 40px
    flex-shrink 0
    z-index 10
    position relative
    // 面板边框
    border 1px solid rgba(0, 242, 255, 0.2)
    border-bottom none // 底部用底线代替
    background linear-gradient(180deg, rgba(8, 18, 60, 0.6) 0%, rgba(5, 12, 45, 0.8) 100%)
    box-shadow inset 0 0 30px rgba(0, 242, 255, 0.03)
    margin 10px 20px 0 20px
    border-radius 2px 2px 0 0
    
    // 宽大的平底凹槽发光底线
    // 形状：左横线 - 超长平缓斜坡下凹 - 包含全标题的超长底线 - 超长平缓斜坡上凸 - 右横线
    &::before
      content ''
      position absolute
      bottom -12px  // 下凹深度
      left 0
      right 0
      height 12px   // 与下凹深度一致
      background linear-gradient(90deg, rgba(0, 242, 255, 0.1) 0%, rgba(0, 242, 255, 0.8) 25%, #00f2ff 50%, rgba(0, 242, 255, 0.8) 75%, rgba(0, 242, 255, 0.1) 100%)
      // 用 2px 的粗细切割出折线
      // 距离中心点 550px 处开始下倾，通过高达 80px 的水平跨度极度缓降到平底（深度 12px）
      // 平底横线的长度约为 940px (左右各 470px)
      clip-path polygon(0 0, calc(50% - 550px) 0, calc(50% - 470px) 12px, calc(50% + 470px) 12px, calc(50% + 550px) 0, 100% 0, 100% 2px, calc(50% + 549px) 2px, calc(50% + 469px) 14px, calc(50% - 469px) 14px, calc(50% - 549px) 2px, 0 2px)
      z-index 2
      filter drop-shadow(0 2px 4px rgba(0, 242, 255, 0.6))
    
    // 中间凹槽处填补透明科技底色
    &::after
      content ''
      position absolute
      bottom -10px
      left calc(50% - 470px)
      width 940px
      height 10px
      background rgba(0, 242, 255, 0.03)
      border-bottom 1px solid rgba(0, 242, 255, 0.2)
      // 斜坡大幅拉长至 80px，底边的切角也要同步加大至 75px
      clip-path polygon(0 100%, 75px 0, calc(100% - 75px) 0, 100% 100%)
      z-index 1
    
    .header-left, .header-right
      width 30%
      display flex
      align-items center
      
    .header-left
      justify-content flex-start
      .datetime
        font-size 22px
        color #00f2ff
        font-family 'YouSheBiaoTiHei', sans-serif
        letter-spacing 2px
        text-shadow 0 0 10px rgba(0, 242, 255, 0.5)

    .header-right
      justify-content flex-end
    
    .header-center
      width 40%
      display flex
      justify-content center
      align-items center
      position relative
      .title-glow
        position absolute
        top 50%
        left 50%
        transform translate(-50%, -50%)
        width 800px
        height 160px
        background radial-gradient(ellipse at center, rgba(0, 242, 255, 0.4) 0%, transparent 75%)
        filter blur(20px)
        z-index -1

      .main-title
        font-family 'YouSheBiaoTiHei', sans-serif
        font-size 72px
        color #ffffff
        letter-spacing 10px
        margin 0
        text-shadow 0 0 20px rgba(0, 242, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.3)

.screen-main
  height calc(100% - 95px) // 减去 header 高度(85px) 和 margin-top(10px)
  display flex
  padding 20px 32px
  gap 32px
  box-sizing border-box
  overflow hidden

.screen-column
  height 100%
  display flex
  flex-direction column
  gap 32px
  overflow hidden

.column-left, .column-right
  width 1168px

.column-center
  flex 1 // 实际上剩余空间也是 1168px (3600 - 1168*2 - 24*2 = 1216, 略大于1168)
  display flex
  flex-direction column
  gap 24px

.data-box
  flex 1
  background rgba(7, 13, 56, 0.55)
  border 1px solid rgba(0, 242, 255, 0.35)
  box-shadow inset 0 0 40px rgba(0, 242, 255, 0.08), 0 0 15px rgba(0, 242, 255, 0.15), 0 4px 20px rgba(0, 0, 0, 0.4)
  position relative
  padding 24px
  border-radius 4px
  overflow hidden
  display flex
  flex-direction column
  
  // 科技感边角 - 左上
  &::before
    content ''
    position absolute
    top 0
    left 0
    width 40px
    height 40px
    border-top 2px solid #00f2ff
    border-left 2px solid #00f2ff
    box-shadow 0 0 12px rgba(0, 242, 255, 0.6), inset 0 0 8px rgba(0, 242, 255, 0.3)
    pointer-events none
    z-index 2

  // 科技感边角 - 右下
  &::after
    content ''
    position absolute
    bottom 0
    right 0
    width 40px
    height 40px
    border-bottom 2px solid #00f2ff
    border-right 2px solid #00f2ff
    box-shadow 0 0 12px rgba(0, 242, 255, 0.6), inset 0 0 8px rgba(0, 242, 255, 0.3)
    pointer-events none
    z-index 2

  .box-title
    font-family 'YouSheBiaoTiHei', sans-serif
    font-weight normal
    font-size 34px
    color #ffffff
    margin 0 0 20px 0
    padding-left 15px
    border-left 5px solid #00f2ff
    line-height 1
    flex-shrink 0
    text-shadow 0 0 10px rgba(0, 242, 255, 0.3)

  .box-content
    flex 1
    width 100%
    overflow hidden
    position relative

.box-header-tabs
  display flex
  justify-content space-between
  align-items center
  margin-bottom 15px
  flex-shrink 0
  
  .box-title
    margin 0 !important
    
  // 顶部大Tab样式
  .header-buttons
    display flex
    gap 12px
    
    .tab-btn
      padding 4px 18px
      font-size 15px
      font-weight normal
      color rgba(255, 255, 255, 0.6)
      background transparent
      border 1px solid transparent
      cursor pointer
      transition all 0.3s
      position relative
      border-radius: 4px
      
      &:hover
        color #00f2ff
        
      &.active
        color #fff
        font-weight bold
        background rgba(0, 10, 40, 0.8)
        border 1px solid #00f2ff
        box-shadow 0 0 10px rgba(0, 242, 255, 0.4) inset
        text-shadow 0 0 5px rgba(255, 255, 255, 0.5)

// 教师画像 - 内部小Tab
.sub-tabs-teacher
  display flex
  justify-content center
  gap 10px
  margin-bottom 10px
  
  .sub-tab-item
    font-size 12px
    color rgba(255, 255, 255, 0.6)
    padding 2px 8px
    cursor pointer
    border 1px solid rgba(255, 255, 255, 0.1)
    border-radius 10px
    &.active
      color #00f2ff
      border-color #00f2ff
      background rgba(0, 242, 255, 0.1)

// 名特优照片墙（还原接近原系统的卡片风格）
.photo-grid
  display grid
  grid-template-columns repeat(5, 1fr)
  /* 使用最小高度保证每行有足够空间显示两行文字，避免第二行底部被裁切 */
  grid-auto-rows minmax(160px, auto)
  gap 10px // 调整间距
  height 100%
  padding 6px 8px 12px 8px // 增加底部内边距避免被滚动条遮挡
  overflow-y auto
  overflow-x hidden
  padding-right 6px /* Prevent scrollbar from covering content */
  align-content start

  /* 自定义滚动条样式 */
  &::-webkit-scrollbar
    width 6px
    background rgba(0, 0, 0, 0.2)
  
  &::-webkit-scrollbar-thumb
    background rgba(0, 242, 255, 0.3)
    border-radius 3px
    &:hover
      background rgba(0, 242, 255, 0.6)
  
  &::-webkit-scrollbar-track
    background transparent

  .photo-item
    background linear-gradient(180deg, rgba(2,10,30,0.7), rgba(2,10,30,0.85))
    border-radius 8px
    border 2px solid rgba(44, 156, 238, 0.8)
    box-shadow 0 0 15px rgba(44, 156, 238, 0.4), inset 0 0 20px rgba(44, 156, 238, 0.2)
    display flex
    flex-direction column
    align-items center
    justify-content flex-start
    padding 8px
    box-sizing border-box
    transition all 0.18s
    min-height 160px
    
    &:hover
      transform translateY(-2px)
      border-color rgba(0, 242, 255, 1)
      box-shadow 0 0 25px rgba(0, 242, 255, 0.6), inset 0 0 30px rgba(0, 242, 255, 0.3)
      z-index 10

    .photo-img
      width 70px
      height 90px
      margin-bottom 4px
      display block
      background-size cover
      background-position center top
      background-repeat no-repeat
      border-radius 4px
      border 1px solid rgba(0, 242, 255, 0.2)
      background-color rgba(0, 0, 0, 0.2)
      box-shadow 0 6px 18px rgba(0, 0, 0, 0.5), 0 0 18px rgba(0, 242, 255, 0.08) inset
      flex-shrink 0
      overflow hidden
      img
        width 100%
        height 100%
        object-fit cover
        display block

    .photo-info
      display flex
      flex-direction column
      align-items center
      justify-content center
      width 100%
      margin-top 0
      gap 2px
      text-align center
      flex 1
      min-height 48px
      .p-name
        font-size 18px
        color #ffffff
        font-weight normal
        margin 0
        font-family 'YouSheBiaoTiHei', sans-serif
        line-height 1.05
        display flex
        align-items center
        justify-content center
        gap 8px
        -webkit-font-smoothing antialiased
        text-rendering optimizeLegibility
        letter-spacing 1px
        /* Keep only subtle shadow to avoid washout */
        text-shadow 0 1px 2px rgba(0,0,0,0.6)
        text-align center
        .p-name-text
          display inline-block
          max-width 68%
          white-space nowrap
          overflow hidden
          text-overflow ellipsis
        .p-dept-inline
          font-size 12px
          color rgba(255,255,255,0.78)
          margin-left 6px
          white-space nowrap
          overflow hidden
          text-overflow ellipsis
      .p-title
        font-size 12px
        color rgba(255, 255, 255, 0.85)
        background rgba(0, 70, 150, 0.55)
        padding 2px 6px
        border-radius 8px
        text-align center
        white-space nowrap
        overflow hidden
        text-overflow ellipsis
        max-width 95%

      .p-dept
        font-size 11px
        color rgba(255,255,255,0.7)
        margin-top 3px
        max-width 95%
        display -webkit-box
        -webkit-line-clamp 2
        -webkit-box-orient vertical
        overflow hidden
        text-overflow ellipsis
        line-height 1.15
        word-break break-word

    // photo-item block ends here


// 教师概况 - 3列并排图表
.chart-overview-container
  display flex
  justify-content space-between
  align-items center
  height 100%
  width 100%
  padding 0 10px
  
  .chart-item-wrapper
    flex 1
    height 100%
    position relative
    
    .chart-ref
      width 100%
      height 100%
      
// 协约之星
.star-student-grid
  display grid
  grid-template-columns repeat(2, 1fr)
  gap 10px
  padding 10px
  
  .star-item
    display flex
    align-items center
    background rgba(255, 215, 0, 0.05)
    border 1px solid rgba(255, 215, 0, 0.2)
    padding 10px
    .star-icon
      font-size 24px
      color #ffd700
      margin-right 10px
    .star-info
      .s-name
        font-size 16px
        color #fff
      .s-desc
        font-size 12px
        color rgba(255, 255, 255, 0.6)

.chart-container
  height 100%
  display flex
  flex-direction column


.tab-segment
  height 100%
  width 100%

.chart-wrapper
  width 100%
  height 300px // 减小高度以适应整体比例

.stats-grid
  display grid
  grid-template-columns repeat(2, 1fr)
  gap 30px
  padding 10px

  .stat-item
    background rgba(0, 242, 255, 0.05)
    padding 20px
    border-radius 4px
    text-align center
    .stat-value
      font-size 48px
      color #00f2ff
      font-weight bold
      small
        font-size 18px
        margin-left 5px
    .stat-label
      color rgba(255, 255, 255, 0.6)
      font-size 20px
      margin-top 10px

.carousel-container
  flex 1 // 自动填充剩余空间
  min-height 0 // 允许压缩
  border-radius 12px
  overflow hidden
  border 2px solid rgba(0, 242, 255, 0.3)
  
  .carousel-img-placeholder
    width 100%
    height 100%
    background rgba(0, 242, 255, 0.05)
    background-size cover
    background-position center
    display flex
    justify-content center
    align-items center
    .placeholder-text
      font-size 48px
      color rgba(0, 242, 255, 0.2)

.school-intro
  background rgba(0, 242, 255, 0.05)
  padding 15px 20px
  border-radius 8px
  margin 10px 0 // 恢复一点margin
  height auto // 自适应内容高度
  flex-shrink 0 // 防止被压缩
  overflow visible // 移除滚动条
  
  p
    font-size 16px
    color rgba(255, 255, 255, 0.8)
    line-height 1.6
    margin 0
    text-indent 2em

  display flex
  align-items flex-start
  flex-direction column
  
  .box-title
    margin-bottom 10px

  .top-tabs
    display flex
    gap 15px
    position absolute
    top 24px
    right 24px
    .tab-item
      font-size 14px
      color rgba(255, 255, 255, 0.6)
      cursor pointer
      padding-bottom 4px
      &.active
        color #00f2ff
        border-bottom 2px solid #00f2ff

.sub-tabs
  display flex
  gap 10px
  margin-bottom 10px
  padding 0 10px
  .sub-tab-item
    font-size 14px
    color rgba(255, 255, 255, 0.6)
    cursor pointer
    padding 4px 12px
    background rgba(255, 255, 255, 0.05)
    border-radius 12px
    transition all 0.3s
    &.active
      background #00f2ff
      color #000
      font-weight bold

.leaderboard
  display flex
  flex-direction column
  gap 8px
  padding 0 10px

  .leader-header
    display flex
    padding 0 20px
    font-size 14px
    color rgba(0, 242, 255, 0.6)
    margin-bottom 5px
    .col-rank { width: 50px; text-align: center; }
    .col-name { flex: 1; }
    .col-class { width: 100px; }
    .col-score { width: 80px; text-align: right; padding-right: 10px; }

  .leader-item
    display flex
    align-items center
    background rgba(0, 242, 255, 0.03)
    padding 10px 0
    border-radius 2px
    font-size 16px
    color #fff
    border-bottom 1px solid rgba(0, 242, 255, 0.05)
    
    .rank
      width 50px
      text-align: center
      color #00f2ff
      font-weight bold
      i { font-size: 18px; }
      
    .name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-left 10px; }
    .class { width: 100px; color: rgba(255, 255, 255, 0.5); font-size: 14px; }
    .stars
      width 80px
      display flex
      justify-content flex-end
      padding-right 10px
      gap 2px
      font-family 'Roboto', sans-serif
      font-weight bold
      color #00f2ff
      
      .fa-star
        font-size 12px
        color rgba(255, 255, 255, 0.1)
        &.star-filled
          color #ffd700
          text-shadow 0 0 8px rgba(255, 215, 0, 0.6)

.campus-fresh-wind-wrapper
  display flex
  flex-direction column
  height 100%
  
  .campus-top-section
    flex 1
    min-height 0
    display flex
    flex-direction column
    background rgba(0, 15, 40, 0.6)
    border 1px solid rgba(0, 242, 255, 0.4)
    border-radius 6px
    box-shadow 0 0 15px rgba(0, 242, 255, 0.1) inset
    padding 14px 16px 8px 16px
    margin-bottom 24px

    .campus-top-imgs
      flex 1
      min-height 0
      position relative
      overflow hidden
      border-radius 4px
      
      .carousel-container
        position absolute
        top 0
        left 0
        width 100%
        height 100%

        .carousel-img-placeholder
          width 100%
          height 100%
          background rgba(0, 242, 255, 0.05)
          background-size cover
          background-position center
          display flex
          justify-content center
          align-items center
          .placeholder-text
            font-size 48px
            color rgba(0, 242, 255, 0.2)

    .school-intro-box
      width 100%
      padding 14px 4px 6px 4px
      box-sizing border-box
      flex-shrink 0
      
      p
        font-size 15px
        color rgba(255, 255, 255, 0.95)
        line-height 1.8
        margin 0
        text-indent 0
        text-align justify


  .fresh-wind-hex-grid
    display flex
    justify-content space-between
    align-items flex-end
    padding-bottom 12px
    flex-shrink 0
    position relative
    z-index 1

    .hex-wrap
      display flex
      flex-direction column
      align-items center
      position relative

    .hex-item
      width 86px
      height 74px
      background rgba(0, 242, 255, 0.9)
      clip-path polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)
      display flex
      justify-content center
      align-items center
      transition all 0.3s
      position relative
      z-index 2

      &:hover
        transform translateY(-4px)
        z-index 3

    .hex-inner
      width 84px
      height 72px
      background rgba(0, 10, 30, 0.95)
      clip-path polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)
      display flex
      justify-content center
      align-items center
      position relative

    .hex-circle
      position absolute
      top 50%
      left 50%
      margin-top -30px
      margin-left -30px
      width 60px
      height 60px
      border-radius 50%
      box-sizing border-box
      border 4px solid rgba(0, 242, 255, 0.2)
      border-right-color rgba(0, 242, 255, 1)
      border-bottom-color rgba(0, 242, 255, 1)
      border-left-color rgba(0, 242, 255, 0.2)
      border-top-color transparent
      transform rotate(45deg)
      
    .stat-num
      font-family 'YouSheBiaoTiHei', sans-serif
      font-size 22px
      color #fff
      z-index 3
      text-shadow 0 0 8px rgba(0, 242, 255, 0.8)

    .hex-label
      margin-top 2px
      background linear-gradient(to right, rgba(0, 20, 60, 0.6), rgba(0, 40, 100, 0.8), rgba(0, 20, 60, 0.6))
      border 1px solid rgba(0, 242, 255, 0.3)
      color rgba(255, 255, 255, 0.9)
      font-size 13px
      font-weight normal
      padding 4px 14px
      z-index 4
      white-space nowrap
      border-radius 2px


.bottom-nav
  display flex
  justify-content center
  gap 24px
  margin-top auto
  padding-bottom 20px
  flex-shrink 0

.hexagon-item
  width 180px
  height 60px
  background linear-gradient(135deg, rgba(3, 30, 80, 0.9), rgba(0, 10, 30, 0.95))
  border 1px solid rgba(0, 242, 255, 0.3)
  box-shadow 0 0 15px rgba(0, 242, 255, 0.1) inset
  transform skewX(-15deg) // Tech slant
  display flex
  justify-content center
  align-items center
  cursor pointer
  position relative
  transition all 0.3s ease
  margin 0 10px

  &::before
    content ''
    position absolute
    top 0
    left 0
    width 8px
    height 8px
    border-top 2px solid #00f2ff
    border-left 2px solid #00f2ff

  &::after
    content ''
    position absolute
    bottom 0
    right 0
    width 8px
    height 8px
    border-bottom 2px solid #00f2ff
    border-right 2px solid #00f2ff

  &:hover
    background rgba(0, 242, 255, 0.15)
    box-shadow 0 0 20px rgba(0, 242, 255, 0.4) inset, 0 0 10px rgba(0, 242, 255, 0.5)
    border-color rgba(0, 242, 255, 0.8)
    transform skewX(-15deg) translateY(-2px) // Lift effect

  .hexagon-inner
    transform skewX(15deg) // Un-skew content
    display flex
    align-items center
    gap 10px
    pointer-events none
    
    i
      font-size 22px
      color #00f2ff
      text-shadow 0 0 5px rgba(0, 242, 255, 0.5)
      
    .nav-label
      font-size 18px
      color #fff
      font-family 'YouSheBiaoTiHei'
      letter-spacing 1px

/* 教师概况布局样式 */
.teacher-overview-layout {
  display: flex;
  height: 100%;
  gap: 20px;
  padding: 0 10px;
}

.teacher-overview-layout .stats-panel {
  width: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
  padding: 20px 10px;
}

.teacher-overview-layout .stat-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.teacher-overview-layout .stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(0, 242, 255, 0.2), rgba(0, 85, 255, 0.2));
  border: 2px solid rgba(0, 242, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #00f2ff;
  padding: 12px; /* Added padding to constrain SVG size */
  box-sizing: border-box;
}

.teacher-overview-layout .stat-icon.male {
  background: linear-gradient(135deg, rgba(0, 157, 255, 0.2), rgba(0, 85, 255, 0.2));
  border-color: rgba(0, 157, 255, 0.5);
  color: #009dff;
}

.teacher-overview-layout .stat-icon.female {
  background: linear-gradient(135deg, rgba(255, 0, 200, 0.2), rgba(164, 0, 255, 0.2));
  border-color: rgba(255, 0, 200, 0.5);
  color: #ff00c8;
}

.teacher-overview-layout .stat-info {
  flex: 1;
}

.teacher-overview-layout .stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 5px;
}

.teacher-overview-layout .stat-value {
  font-size: 32px;
  font-weight: bold;
  color: #00f2ff;
  font-family: 'YouSheBiaoTiHei', sans-serif;
}

.teacher-overview-layout .chart-middle {
  flex: 2;
  height: 100%;
}

.teacher-overview-layout .chart-middle .chart-ref {
  width: 100%;
  height: 100%;
}

.teacher-overview-layout .chart-right {
  flex: 1;
  height: 100%;
}

.teacher-overview-layout .chart-right .chart-ref {
  width: 100%;
  height: 100%;
}

/* 三图表容器样式 */
.chart-triple-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 0 10px;
  gap: 20px;
}

.chart-triple-container .chart-third {
  flex: 1;
  height: 100%;
  position: relative;
}

.chart-triple-container .chart-third .chart-ref {
  width: 100%;
  height: 100%;
}

/* --- 学校画像子模块样式 --- */
/* --- 班级画像增强样式 --- */
.tc-photo-wrapper
  width 120px
  height 150px
  border-radius 8px
  overflow hidden
  border 2px solid rgba(0, 242, 255, 0.3)
  background rgba(0, 0, 0, 0.2)
  flex-shrink 0

  .tc-photo-img
    width 100%
    height 100%
    object-fit cover
    display block

  .tc-photo-placeholder
    width 100%
    height 100%
    display flex
    align-items center
    justify-content center
    font-size 40px
    color rgba(0, 242, 255, 0.2)

.cp-img-wrapper
  width 100%
  height 100%
  display flex
  align-items center
  justify-content center
  overflow hidden
  border-radius 8px
  background rgba(0, 0, 0, 0.1)

  img
    width 100%
    height 100%
    object-fit contain /* 班徽通常建议 contain，合照建议 cover，这里统一处理 */
    display block
    image-rendering -webkit-optimize-contrast

.org-chart-container
  width 100%
  height 100%
  padding 10px
  display flex
  align-items center
  justify-content center
  
  .org-image
    width 100%
    height 100%
    object-fit contain
    border: 1px solid rgba(0, 242, 255, 0.3);
    box-shadow: 0 0 20px rgba(0, 242, 255, 0.1);
    background: rgba(0, 10, 30, 0.5);
    padding: 10px;
    border-radius: 4px;

.school-overview-layout
  display flex
  width 100%
  height 100%

.overview-left
  width 50%
  display flex
  flex-direction column
  padding 10px

.overview-right
  width 50%
  position relative
  height 100%
  
  .chart-ref
    width 100%
    height 100%

.stats-card-grid
  display grid
  grid-template-columns 1fr 1fr
  grid-template-rows 1fr 1fr
  gap 15px
  flex 1

/* --- 班级画像子模块样式 --- */
.class-profile .box-header-tabs {
  margin-bottom: 0;
  border-bottom: 1px solid rgba(0, 242, 255, 0.1);
  padding-bottom: 15px;
}

.header-right-tools {
  display: flex;
  align-items: center;
  gap: 20px;
}

.class-selector {
  // 移除原有Flex布局，改为由el-cascader接管
  padding: 0;
  background: transparent;
  border: none;
  
  ::v-deep .el-input__inner {
    background-color: rgba(0, 78, 146, 0.4);
    border: 1px solid rgba(0, 242, 255, 0.3);
    color: #00f2ff;
    font-family: 'YouSheBiaoTiHei';
    font-size: 16px;
    height: 36px;
    line-height: 36px;
    border-radius: 4px;
    padding-left: 15px;
    &::placeholder {
      color: rgba(0, 242, 255, 0.5);
    }
  }
  
  ::v-deep .el-cascader__dropdown {
    background-color: rgba(3, 5, 30, 0.95);
    border: 1px solid rgba(0, 242, 255, 0.3);
    box-shadow: 0 0 20px rgba(0, 242, 255, 0.2);
  }
}
</style>


<style lang="stylus">
/* 全局样式覆盖 ElementUI Cascader Popper */
.tech-cascader-popper
  background-color rgba(3, 5, 30, 0.95) !important
  border 1px solid rgba(0, 242, 255, 0.3) !important
  box-shadow 0 0 20px rgba(0, 242, 255, 0.2) !important
  box-shadow 0 0 20px rgba(0, 242, 255, 0.2) !important
  
  /* Hide the little arrow on top for a cleaner look */
  .popper__arrow
    display none !important
  
  .el-cascader-menu
    border-right 1px solid rgba(0, 242, 255, 0.1)
    color #fff
    min-width 240px !important /* Added min-width to balance the wide input */
    
    .el-cascader-node
      &:hover, &.is-active
        background-color rgba(0, 242, 255, 0.1) !important
        color #00f2ff !important
        
      &[aria-expanded="true"]
        color #00f2ff


</style>

<style lang="stylus" scoped>
.class-profile-content {
  display: flex;
  padding: 20px 10px 10px 10px; /* Reduced bottom padding from 20px to 0 */
  gap: 20px;
  height: 100%;
}

/* 左侧班主任信息 */
.cp-left {
  width: 40%;
  height: 100%;
}

.teacher-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px; /* Reduced from 20px */
}

.tc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px; /* Reduced from 15px */
  border-bottom: 1px solid rgba(0, 242, 255, 0.1);
}

.tc-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tc-name
  font-size 36px
  color #00f2ff
  font-family 'YouSheBiaoTiHei'
  text-shadow 0 0 10px rgba(0, 242, 255, 0.5)
  letter-spacing 2px

.tc-role
  font-size 16px
  color #fff
  background linear-gradient(90deg, rgba(0, 242, 255, 0.2), rgba(0, 242, 255, 0))
  padding 4px 15px
  border-left 3px solid #00f2ff
  border-radius 0 4px 4px 0
  display inline-block
  margin-top 5px

.tc-photo-wrapper
  width 180px
  display flex
  align-items center
  justify-content center

.tc-photo-img
  width 100%
  height auto
  border 1px solid rgba(0, 242, 255, 0.5)
  box-shadow 0 0 15px rgba(0, 242, 255, 0.2)
  object-fit contain
  border-radius 4px

.tc-photo-placeholder
  width 180px
  height 110px /* Reduced from 135px */
  background-color rgba(0, 242, 255, 0.05)
  border 1px solid rgba(0, 242, 255, 0.3)
  display flex
  align-items center
  justify-content center
  box-shadow 0 0 15px rgba(0, 242, 255, 0.1) inset
  
  i
    font-size 50px
    color rgba(0, 242, 255, 0.4)
    text-shadow 0 0 10px rgba(0, 242, 255, 0.3)

.tc-message
  flex 1
  background rgba(0, 78, 146, 0.2)
  border 1px solid rgba(0, 242, 255, 0.2)
  border-radius 8px
  padding 20px 30px 30px 30px /* Reduced top to 20px, kept bottom 30px */
  display flex
  flex-direction column
  gap 15px
  position relative
  overflow hidden
  
  &::before
    content '"'
    position absolute
    top 10px
    left 10px
    font-size 60px
    line-height 1
    color rgba(0, 242, 255, 0.1)
    font-family 'YouSheBiaoTiHei'

.tm-title
  font-size 22px
  color #ffd700
  font-family 'YouSheBiaoTiHei'
  text-shadow 0 0 5px rgba(255, 215, 0, 0.3)
  position relative
  z-index 1

.tm-content
  font-size 16px
  color rgba(255, 255, 255, 0.9)
  line-height 1.8
  text-indent 2em
  text-align justify
  position relative
  z-index 1

/* 右侧轮播 */
.cp-right
  width 60%
  height 100%

.cp-carousel-container
  height 100%
  border 1px solid rgba(0, 242, 255, 0.3)
  border-radius 8px
  overflow hidden
  position relative
  background rgba(0, 0, 0, 0.3)
  display flex
  flex-direction column
  box-shadow 0 0 20px rgba(0, 242, 255, 0.1)

.cp-carousel-content
  flex 1
  overflow hidden
  height 100%
  
  ::v-deep .el-carousel
    height 100%
    
  ::v-deep .el-carousel__container
    height 100%


.cp-carousel-indicators {
  height: 40px;
  display: flex;
  background: rgba(0, 0, 0, 0.5);
  border-top: 1px solid rgba(0, 242, 255, 0.2);
}

.cp-indicator-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.3s;
  font-size: 16px;
  
  &:hover {
    color: #fff;
    background: rgba(0, 242, 255, 0.1);
  }
  
  &.active {
    color: #00f2ff;
    background: rgba(0, 242, 255, 0.2);
    font-weight: bold;
    box-shadow: inset 0 -3px 0 #00f2ff;
  }
}

.cp-slide-item {
  width: 100%;
  height: 100%;
  position: relative;
}
.cp-img-wrapper
  width 100%
  height 100%
  display flex
  align-items center
  justify-content center
  background-color rgba(0, 0, 0, 0.4)
  padding 10px
  
  img
    max-width 100%
    max-height 100%
    object-fit contain

.placeholder-container
  display flex !important
  flex-direction column !important
  align-items center !important
  justify-content center !important
  gap 15px !important
  width 100%
  height 100%
  
  i
    font-size 80px !important
    color rgba(0, 242, 255, 0.2) !important
    text-shadow 0 0 15px rgba(0, 242, 255, 0.3) !important
    animation pulse-glow 3s infinite alternate
  
  .placeholder-text
    font-size 20px !important
    color rgba(0, 242, 255, 0.6) !important
    letter-spacing 4px !important
    font-family 'YouSheBiaoTiHei' !important

// 全局空状态占位：居中显示大号提示，用于名特优/教师光荣榜/教师形象墙等
.empty-placeholder
  display flex
  align-items center
  justify-content center
  flex-direction column
  height 100%
  width 100%
  text-align center

  i
    font-size 60px
    color rgba(0,242,255,0.15)
    margin-bottom 8px

  .placeholder-text
    font-size 24px
    color #00f2ff
    letter-spacing 4px
    font-family 'YouSheBiaoTiHei', sans-serif
    text-shadow 0 0 8px rgba(0,242,255,0.25)
    text-shadow 0 0 10px rgba(0, 242, 255, 0.2) !important

@keyframes pulse-glow
  0%
    text-shadow 0 0 10px rgba(0, 242, 255, 0.2)
    opacity 0.7
  100%
    text-shadow 0 0 25px rgba(0, 242, 255, 0.6)
    opacity 1


.stat-card {
  background: rgba(0, 78, 146, 0.2);
  border: 1px solid rgba(0, 242, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 15px;
  box-shadow: inset 0 0 10px rgba(0, 242, 255, 0.05);
}

/* --- 学生画像样式 --- */
.student-profile
  .header-left
    display flex
    align-items center
    gap 20px
    
  .student-class-selector
    // 复用 class-selector 样式，这里只需调整特定样式
    ::v-deep .el-input__inner
      width 480px !important

// 增加 class-profile 中选择器的宽度
.class-profile
  .class-selector
    ::v-deep .el-input__inner
      width 480px !important

.leaderboard-container
  width 100%
  height 100%
  display flex
  flex-direction column

.leader-columns
  display flex
  width 100%
  height 100%
  gap 20px
  
.leader-col
  flex 1
  display flex
  flex-direction column
  background rgba(0, 78, 146, 0.2)
  border 1px solid rgba(0, 242, 255, 0.2)
  border-radius 8px
  overflow hidden
  
  .col-title
    height 40px
    line-height 40px
    text-align center
    background rgba(0, 242, 255, 0.1)
    color #00f2ff
    font-size 20px
    font-family 'YouSheBiaoTiHei'
    border-bottom 1px solid rgba(0, 242, 255, 0.2)
    text-shadow 0 0 10px rgba(0, 242, 255, 0.5)
    
  .col-header
    display flex
    height 36px
    align-items center
    background rgba(0, 0, 0, 0.2)
    color rgba(255, 255, 255, 0.7)
    font-size 14px
    border-bottom 1px solid rgba(0, 242, 255, 0.1)
    padding 0 10px
    
    span
      text-align center
      
    .h-rank
      width 50px
    .h-name
      flex 1
    .h-score
      width 60px
      
  .col-list-scroll
    flex 1
    overflow-y auto /* Changed from hidden to auto */
    position relative
    
    /* Hide scrollbar for Chrome/Safari/Opera */
    &::-webkit-scrollbar
      width 0px
      background transparent
    
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style none  /* IE and Edge */
    scrollbar-width none  /* Firefox */
    
    .list-item
      display flex
      height 40px
      align-items center
      padding 0 10px
      border-bottom 1px solid rgba(255, 255, 255, 0.05)
      color #fff
      font-size 16px
      transition all 0.3s
      
      &:hover
        background rgba(0, 242, 255, 0.1)
      
      &:nth-child(even)
        background rgba(255, 255, 255, 0.02)
        
      span
        text-align center
        
      .rank
        width 50px
        font-family 'YouSheBiaoTiHei'
        font-size 22px
        color rgba(255, 255, 255, 0.5)
        font-style italic
        
        i
          font-size 22px
          text-shadow 0 0 10px currentColor
          
      .name
        flex 1
        
      .score
        width 60px
        color #00f2ff
        font-weight bold
        font-family 'YouSheBiaoTiHei'
        font-size 20px
        text-shadow 0 0 8px rgba(0, 242, 255, 0.5)

// 单项榜样式
.single-col
  .col-list-scroll
    padding 10px
    
  .single-item-group
    margin-bottom 15px
    background rgba(0, 0, 0, 0.2)
    border 1px solid rgba(0, 242, 255, 0.1)
    border-radius 4px
    overflow hidden
    
    .group-header
      padding 8px 15px
      background rgba(0, 242, 255, 0.1)
      display flex
      justify-content space-between
      align-items center
      border-bottom 1px solid rgba(0, 242, 255, 0.1)
      
      .g-name
        color #fff
        font-size 16px
        font-weight bold
        
      .g-score
        color #ffd700
        font-family 'YouSheBiaoTiHei'
        font-size 20px
        text-shadow 0 0 8px rgba(255, 215, 0, 0.4)
        
    .group-children
      padding 5px 0
      
      .child-item
        display flex
        padding 8px 15px
        align-items center
        color rgba(255, 255, 255, 0.8)
        font-size 14px
        border-bottom 1px dashed rgba(255, 255, 255, 0.1)
        transition background 0.3s
        
        &:hover
          background rgba(255, 255, 255, 0.05)
        
        &:last-child
          border-bottom none
          
        .c-rank
          width 30px
          color rgba(255, 255, 255, 0.5)
          font-family 'YouSheBiaoTiHei'
          
        .c-name
          flex 1
          
        .c-score
          width 40px
          text-align right
          color #fff
          font-family 'YouSheBiaoTiHei'
          font-size 16px

// 协约之星样式
.star-student-list
  width 100%
  height 100%
  display flex
  flex-direction column
  background rgba(0, 78, 146, 0.2)
  border 1px solid rgba(0, 242, 255, 0.2)
  border-radius 8px
  overflow hidden
  
  .star-list-header
    display flex
    height 45px
    align-items center
    background rgba(0, 242, 255, 0.1)
    color #00f2ff
    font-size 18px
    font-family 'YouSheBiaoTiHei'
    border-bottom 1px solid rgba(0, 242, 255, 0.2)
    padding 0 20px
    text-shadow 0 0 10px rgba(0, 242, 255, 0.5)
    
    span
      text-align center
      flex 1
      
    .h-rank
      // width 80px -> flex 1 via span
    .h-name
      // flex 1 via span
    .h-score
      // width 100px -> flex 1 via span
      
  .star-list-content
    flex 1
    overflow-y auto /* Changed from hidden to auto */
    position relative

    /* Hide scrollbar for Chrome/Safari/Opera */
    &::-webkit-scrollbar
      width 0px
      background transparent
    
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style none  /* IE and Edge */
    scrollbar-width none  /* Firefox */
    
    .star-row
      display flex
      height 50px
      align-items center
      padding 0 20px
      border-bottom 1px solid rgba(255, 255, 255, 0.05)
      color #fff
      font-size 16px
      transition all 0.3s
      
      &:hover
        background rgba(0, 242, 255, 0.1)
      
      &:nth-child(even)
        background rgba(255, 255, 255, 0.02)
        
      span
        text-align center
        flex 1
        
      .rank
        // width 80px -> flex 1
        font-family 'YouSheBiaoTiHei'
        font-size 24px
        color rgba(255, 255, 255, 0.5)
        font-style italic
        
      .name
        // flex 1
        
      .score
        // width 100px -> flex 1
        color #ffd700
        font-family 'YouSheBiaoTiHei'
        font-size 22px
        text-shadow 0 0 8px rgba(255, 215, 0, 0.4)

// Class Profile Header Layout
.box-header-tabs
  display flex
  justify-content space-between
  align-items center

.class-profile
  .header-left
    display flex
    align-items center
    gap 20px
.stat-card
  background rgba(0, 78, 146, 0.2)
  border 1px solid rgba(0, 242, 255, 0.2)
  border-radius 8px
  display flex
  align-items center
  padding 15px
  box-shadow inset 0 0 10px rgba(0, 242, 255, 0.05)
  transition all 0.3s
  
  &:hover
    background rgba(0, 242, 255, 0.1)
    border-color rgba(0, 242, 255, 0.5)
    box-shadow 0 0 15px rgba(0, 242, 255, 0.2)

  .sc-icon
    width 50px
    height 50px
    border-radius 50%
    background rgba(0, 242, 255, 0.1)
    display flex
    align-items center
    justify-content center
    margin-right 15px
    border 1px solid rgba(0, 242, 255, 0.3)
    
    i
      color #00f2ff
      font-size 24px
      text-shadow 0 0 5px rgba(0, 242, 255, 0.5)

  .sc-info
    display flex
    flex-direction column
    
  .sc-val
    font-family 'YouSheBiaoTiHei'
    font-size 28px
    color #fff
    text-shadow 0 0 10px rgba(0, 242, 255, 0.5)
    line-height 1.2
    
  .sc-lbl
    font-size 14px
    color rgba(255, 255, 255, 0.6)
    margin-top 2px

.ratio-section
  height 60px
  display flex
  align-items center
  justify-content center
  margin-top 15px
  background rgba(0, 0, 0, 0.2)
  border-radius 30px
  border 1px solid rgba(0, 242, 255, 0.3)
  box-shadow 0 0 15px rgba(0, 242, 255, 0.1)

.ratio-bar
  font-size 16px
  color #fff
  
  .highlight
    color #ffd700
    font-family 'YouSheBiaoTiHei'
    font-size 22px
    margin 0 5px
    text-shadow 0 0 8px rgba(255, 215, 0, 0.4)
    
  .standard
    color #ff6b6b
    font-family 'YouSheBiaoTiHei'
    font-size 22px
    margin 0 5px
    text-shadow 0 0 8px rgba(255, 107, 107, 0.4)

.school-gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 15px;
  height: 100%;
  padding: 10px;
}
.gallery-item {
  border: 1px solid rgba(0, 242, 255, 0.3);
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}
.gallery-item .g-img {
  flex: 1;
  background-size: cover;
  background-position: center;
  background-color: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.gallery-item .placeholder-icon {
  font-size: 30px;
  color: rgba(255,255,255,0.2);
}
.gallery-item .g-title {
  height: 30px;
  line-height: 30px;
  text-align: center;
  background: rgba(0, 20, 60, 0.8);
  color: #fff;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 8px;
}

/* 中间列选项卡 */
.center-tabs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  border: 1px solid rgba(0, 242, 255, 0.3);
  border-radius: 4px;
  padding: 5px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  background-color: rgba(3, 10, 36, 0.5);
  position: relative;
  z-index: 10;
}

.center-tab-btn {
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  padding: 8px 20px;
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 4px;
}

.center-tab-btn:hover {
  color: #fff;
}

.center-tab-btn.active {
  color: #fff;
  background-color: rgba(0, 242, 255, 0.2);
  border: 1px solid rgba(0, 242, 255, 0.5);
}

.center-content {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
}

.center-module {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>

<style lang="stylus" scoped>
/* Tech Icon Styles - Added for Redesign */
.tech-svg-icon
  width 100%
  height 100%
  filter drop-shadow(0 0 2px rgba(0, 242, 255, 0.5))

.stat-card 
  .sc-icon.tech-icon-container
    width 60px  /* Reduced from 80px */
    height 60px /* Reduced from 80px */
    border-radius 50%
    background rgba(0, 78, 146, 0.2)
    border 2px solid rgba(0, 242, 255, 0.3)
    padding 12px /* Reduced form 15px */
    display flex
    justify-content center
    align-items center
    box-shadow inset 0 0 20px rgba(0, 242, 255, 0.1), 0 0 10px rgba(0, 242, 255, 0.2)
    margin-right 15px /* Reduced margin */
    transition all 0.3s ease
    
    &:hover
      transform scale(1.05)
      border-color rgba(0, 242, 255, 0.8)
      box-shadow inset 0 0 20px rgba(0, 242, 255, 0.2), 0 0 20px rgba(0, 242, 255, 0.4)
    
    // Reset internal i styles if any remain or if svg needs it
    svg
      width 100%
      height 100%
</style>